import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Button } from '../../components/ui/button';
import CustomerCombobox from '../../components/CustomerCombobox.jsx';
import DataLoggerSelector from '../../components/DataLoggerSelector';
import MasterCombobox from '../../components/MasterCombobox';
import ContainerDetailsSection from './ContainerDetailsSection';
import { HouseDocketConsolidationPicker } from '../../components/HouseDocketConsolidationPicker';
// LoadTypeBillingFields removed — only used in ROAD_RAIL_V2 form
import { Plus, Trash2, FileText, Package, Ship, Thermometer, AlertTriangle, ClipboardList, Settings2, Truck, Layers, Plane, ChevronDown, ChevronUp } from 'lucide-react';

// ── Per-mode field label / behaviour config ──────────────────────────────────
const MODE_CONFIG = {
  AIR: {
    mode_label:          'AIR',
    hawb_label:          'HAWB No.',
    hawb_date_label:     'HAWB Date',
    mawb_label:          'MAWB No.',
    mawb_date_label:     'MAWB Date',
    hawb_desc_label:     'HAWB Description',
    mawb_desc_label:     'MAWB Description',
    awb_label:           'AWB No.',
    awb_date_label:      'AWB Date',
    awb_desc_label:      'AWB Description',
    house_section_label: 'HOUSE AIR WAY BILL',
    carrier_type:        'AIRLINE',
    carrier_placeholder: 'Select Airline',
    location_mode:       'AIR',
    vessel_label:        'Flight No',
    vessel_date_label:   'Flight Date',
    vessel_remarks_label:'Remarks',
  },
  SEA: {
    mode_label:          'SEA',
    hawb_label:          'HBL No.',
    hawb_date_label:     'HBL Date',
    mawb_label:          'MBL No.',
    mawb_date_label:     'MBL Date',
    hawb_desc_label:     'HBL Description',
    mawb_desc_label:     'MBL Description',
    awb_label:           'BL No.',
    awb_date_label:      'BL Date',
    awb_desc_label:      'BL Description',
    house_section_label: 'HOUSE BILL OF LADING',
    carrier_type:        'SHIPPING_LINE',
    carrier_placeholder: 'Select Shipping Line',
    location_mode:       'SEA',
    vessel_label:        'Vessel Name / Voyage No.',
    vessel_date_label:   'ETD Date',
    vessel_remarks_label:'Remarks',
  },
  ROAD_RAIL: {
    mode_label:          'ROAD',
    carrier_type:        'ROAD',
    carrier_placeholder: 'Select Vendor',
    location_mode:       null,
    hawb_label:          'LR No.',
    hawb_date_label:     'LR Date',
    mawb_label:          '',
    mawb_date_label:     '',
    hawb_desc_label:     '',
    mawb_desc_label:     '',
    vessel_label:        'Vehicle No.',
    vessel_date_label:   '',
    vessel_remarks_label:'Remarks',
  },
  AIR_V2: {
    mode_label:          'AIR',
    hawb_label:          'HAWB No.',
    hawb_date_label:     'HAWB Date',
    mawb_label:          '',
    mawb_date_label:     '',
    hawb_desc_label:     'HAWB Description',
    mawb_desc_label:     '',
    carrier_type:        'AIRLINE',
    carrier_placeholder: 'Select Airline',
    location_mode:       'AIR',
    vessel_label:        'Flight No',
    vessel_date_label:   'Flight Date',
    vessel_remarks_label:'Remarks',
  },
};

const SCOPE_OF_WORK_OPTIONS = [
  'Custom Clearance', 'Transportation', 'Forwarding', 'Door Delivery',
  'EXW', 'IGM', 'Packaging', 'Warehousing', 'Insurance', 'Data Logger'
];

const EMPTY_DIMENSION = { length: '', width: '', height: '', no_of_packages: '', cbm_chargeable_weight: '' };

const EMPTY_CONTAINER = {
  container_no: '', container_type: '', packages: '',
  gross_weight: '', net_weight: '', c_seal_no: '', l_seal_no: '', cbm: ''
};

// CBM conversion factors to cubic meters (m³)
const CBM_FACTORS = {
  CM: 1 / 1_000_000,          // cm³  → m³
  M:  1,                       // m³   → m³
  MM: 1 / 1_000_000_000,       // mm³  → m³
  IN: 0.000016387064,          // in³  → m³
  FT: 0.028316846592,          // ft³  → m³
};
const DIMENSION_UNITS = [
  { value: 'CM', label: 'Centimeters (CM)' },
  { value: 'M',  label: 'Meters (M)' },
  { value: 'MM', label: 'Millimeters (MM)' },
  { value: 'IN', label: 'Inches (IN)' },
  { value: 'FT', label: 'Feet (FT)' },
];
const calcCBM = (l, w, h, qty, unit) => {
  const factor = CBM_FACTORS[unit] || CBM_FACTORS.CM;
  return (l && w && h && qty) ? (l * w * h * qty * factor).toFixed(4) : '';
};

// Air volumetric weight (kg): convert any unit → cm³, then ÷ divisor
// International IATA: ÷ 6000  |  Domestic India: ÷ 5000
const TO_CM3 = { CM: 1, M: 1_000_000, MM: 0.001, IN: 16.387064, FT: 28316.846592 };
const calcVolWeight = (l, w, h, qty, unit, divisor) => {
  if (!l || !w || !h || !qty) return '';
  const cm3 = TO_CM3[unit] || 1;
  return (l * w * h * cm3 * qty / divisor).toFixed(2);
};

const INVOICE_CURRENCIES = [
  { value: 'INR', label: 'INR — Indian Rupee' },
  { value: 'USD', label: 'USD — US Dollar' },
  { value: 'EUR', label: 'EUR — Euro' },
  { value: 'GBP', label: 'GBP — British Pound' },
  { value: 'AUD', label: 'AUD — Australian Dollar' },
  { value: 'AED', label: 'AED — UAE Dirham' },
  { value: 'SGD', label: 'SGD — Singapore Dollar' },
  { value: 'CNY', label: 'CNY — Chinese Yuan' },
  { value: 'JPY', label: 'JPY — Japanese Yen' },
  { value: 'CAD', label: 'CAD — Canadian Dollar' },
  { value: 'CHF', label: 'CHF — Swiss Franc' },
  { value: 'HKD', label: 'HKD — Hong Kong Dollar' },
  { value: 'SAR', label: 'SAR — Saudi Riyal' },
  { value: 'MYR', label: 'MYR — Malaysian Ringgit' },
  { value: 'THB', label: 'THB — Thai Baht' },
  { value: 'KWD', label: 'KWD — Kuwaiti Dinar' },
  { value: 'OMR', label: 'OMR — Omani Rial' },
  { value: 'QAR', label: 'QAR — Qatari Riyal' },
  { value: 'BHD', label: 'BHD — Bahraini Dinar' },
  { value: 'NZD', label: 'NZD — New Zealand Dollar' },
];

const EMPTY_INVOICE = {
  po_no: '', invoice_no: '', invoice_date: '', invoice_currency: 'INR', invoice_value: '', description: '', sb_be_no: '',
  second_date: '', net_weight: '', gross_weight: '', no_of_packages: '', cbm_chargeable_weight: '', eway_bill_no: '',
  dimension_unit: 'CM',
  special_case_charges: false, airline_actual_weight: '',
  dimensions: [{ ...EMPTY_DIMENSION }]
};

const EMPTY_DELIVERY_INFO = {
  lr_no: '', lr_date: '', vehicle_type: 'Stavan', vehicle_no: '', vendor: '',
  loading_from: '', pickup_date: '',
  clearance_at_origin: '', clearance_date_origin: '',
  clearance_at_destination: '', clearance_date_destination: '',
  delivery_at: '', delivery_date: '',
  value: '', weight: '', packages_type: '', volume_weight: '',
  door_delivery_date: '', delivery_taken_by: '', description: '', remarks: '',
};

// ── Pure helper: invoice-level CBM / Air Vol. Wt. sum ───────────────────────
// AIR → sum Air Volumetric Weight (÷6000 intl, ÷5000 domestic)
// SEA → sum CBM (m³)
const sumChargeableWeight = (dims, unit, mode, intlDomestic) => {
  const isAir = mode === 'AIR' || mode === 'AIR_V2';
  if (isAir) {
    const divisor = intlDomestic === 'Domestic' ? 5000 : 6000;
    const total = dims.reduce((acc, d) => {
      const l = parseFloat(d.length) || 0;
      const w = parseFloat(d.width)  || 0;
      const h = parseFloat(d.height) || 0;
      const q = parseFloat(d.no_of_packages) || 0;
      return acc + (parseFloat(calcVolWeight(l, w, h, q, unit, divisor)) || 0);
    }, 0);
    return total > 0 ? total.toFixed(2) : '';
  }
  // SEA / others — sum CBM (m³)
  const total = dims.reduce((acc, d) => acc + (parseFloat(d.cbm_chargeable_weight) || 0), 0);
  return total > 0 ? total.toFixed(4) : '';
};

// ── Pure helper: sum invoice rows into package_info ─────────────────────────
const computePackageInfo = (invs) => {
  const sumField = (field) => invs.reduce((acc, inv) => acc + (parseFloat(inv[field]) || 0), 0);
  const fmt = (n) => n === 0 ? '' : (n % 1 === 0 ? String(n) : n.toFixed(2));
  return {
    package_no_of_packages:        fmt(sumField('no_of_packages')),
    package_gross_weight:          fmt(sumField('gross_weight')),
    package_net_weight:            fmt(sumField('net_weight')),
    package_cbm_chargeable_weight: fmt(sumField('cbm_chargeable_weight')),
  };
};

const parseNum = (val, isFloat = true) => {
  if (val === null || val === undefined || val === '') return 0;
  if (typeof val === 'number') return isNaN(val) ? 0 : val;
  const cleaned = String(val).replace(/,/g, '').match(/[-+]?(?:\d+(?:\.\d+)?|\.\d+)/);
  if (!cleaned) return 0;
  const num = isFloat ? parseFloat(cleaned[0]) : parseInt(cleaned[0], 10);
  return isNaN(num) ? 0 : num;
};

const AirJobSheetForm = ({
  payload,
  onChange,
  customers,
  customerDetails = {},
  onCustomerSelect,
  locations = [],
  carriers = [],
  agents = [],
  mode = 'AIR',
  schemaKey = '',
  companyId,
  currentDocketId,
  shipmentType,
  inventoryItems = [],
  onInventoryItemsChange,
  excludeDocketId,
}) => {
  const cfg = MODE_CONFIG[mode] || MODE_CONFIG.AIR;
  const isAirV2 = mode === 'AIR_V2';
  const isSea = mode === 'SEA';
  const header = payload.header || {};
  const shipmentInfo = payload.shipment_info || {};
  const shipmentStructure = header.shipment_structure || (
    header.consolidation_job === 'Yes'
      ? 'CONSOLIDATION'
      : (shipmentInfo.hawb_no && shipmentInfo.mawb_no ? 'BACK_TO_BACK' : (shipmentInfo.mawb_no && !shipmentInfo.hawb_no ? 'DIRECT' : 'BACK_TO_BACK'))
  );
  const isConsolidation = shipmentStructure === 'CONSOLIDATION';
  const isDirect = shipmentStructure === 'DIRECT';
  const isBackToBack = shipmentStructure === 'BACK_TO_BACK';
  const consolidation = useMemo(() => payload.consolidation || {}, [payload.consolidation]);
  const linkedHouseDockets = useMemo(() => consolidation.house_dockets_summary || [], [consolidation.house_dockets_summary]);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const isInward = header.inward_outward === 'Inward';
  const invoices = useMemo(() => payload.invoices || [{ ...EMPTY_INVOICE }], [payload.invoices]);
  const packageInfo = payload.package_info || {};



  const billToType = header.bill_to_type || 'customer';
  const exporterType = header.exporter_shipper_type || 'customer';
  const importerType = header.importer_receiver_type || (isConsolidation ? 'agent' : 'customer');
  const notifyType = header.notify_party_type || (isConsolidation ? 'agent' : 'customer');

  const agentMap = useMemo(() => {
    return Object.fromEntries((agents || []).map(a => [a.id, a]));
  }, [agents]);

  const carrierMap = useMemo(() => {
    return Object.fromEntries((carriers || []).map(c => [c.id, c.carrier_name || c.name]));
  }, [carriers]);

  const locationMap = useMemo(() => {
    return Object.fromEntries((locations || []).map(l => [l.id, l.location_code || l.location_name || l.name]));
  }, [locations]);

  const consolTotalPkgs = useMemo(() => {
    return linkedHouseDockets.reduce((sum, d) => sum + parseNum(d.packages, false), 0);
  }, [linkedHouseDockets]);

  const consolTotalGw = useMemo(() => {
    return linkedHouseDockets.reduce((sum, d) => sum + parseNum(d.gross_weight, true), 0);
  }, [linkedHouseDockets]);

  // Handlers for managing consolidation house dockets
  const handleConfirmHouseDockets = useCallback((selectedDockets) => {
    const totalPkgs = selectedDockets.reduce((sum, d) => sum + parseNum(d.packages, false), 0);
    const totalGw = selectedDockets.reduce((sum, d) => sum + parseNum(d.gross_weight, true), 0);
    const linkedIds = selectedDockets.map(d => d.id);

    const updatedConsolidation = {
      is_consolidation: true,
      linked_house_docket_ids: linkedIds,
      house_dockets_summary: selectedDockets,
      total_house_dockets: selectedDockets.length,
      total_packages: totalPkgs,
      total_gross_weight: Math.round(totalGw * 100) / 100
    };

    const currentHeader = payload.header || {};
    let nextHeader = { ...currentHeader };

    // 1. Auto-prefill Scope of Work & Cargo Type from house dockets
    if (selectedDockets.length > 0) {
      const houseScopes = selectedDockets.flatMap(d => Array.isArray(d.scope_of_work) ? d.scope_of_work : []);
      if (houseScopes.length > 0) {
        nextHeader.scope_of_work = Array.from(new Set([...(currentHeader.scope_of_work || []), ...houseScopes]));
      }

      const firstCargoType = selectedDockets.map(d => d.cargo_type).find(Boolean);
      if (firstCargoType) {
        nextHeader.cargo_type = firstCargoType;
      }
    }

    // 2. Auto-prefill Shipment Info (route, clearance, carrier, agent, flight details)
    let nextShipmentInfo = { ...(payload.shipment_info || {}) };
    const firstShipment = selectedDockets.map(d => d.shipment_info).find(s => s && Object.keys(s).length > 0);
    if (firstShipment) {
      const shipmentFieldsToCopy = [
        'place_of_receipts', 'receipt_date',
        'clearance_at_origin', 'clearance_date_origin',
        'clearance_at_destination', 'clearance_date_destination',
        'loading_from', 'loading_date',
        'discharge_at', 'discharge_date',
        'delivery_at', 'delivery_date',
        'carrier', 'agent',
        'prepaid_collect', 'freighted_status',
        'flight_no', 'flight_date', 'flight_remarks'
      ];
      shipmentFieldsToCopy.forEach(k => {
        if (firstShipment[k] !== undefined && firstShipment[k] !== null && firstShipment[k] !== '') {
          nextShipmentInfo[k] = firstShipment[k];
        }
      });

      // Also set Destination Agent in Header if present and currently empty
      if (firstShipment.agent && (!nextHeader.importer_receiver || nextHeader.importer_receiver_type === 'agent')) {
        nextHeader.importer_receiver_type = 'agent';
        nextHeader.importer_receiver = firstShipment.agent;
      }
      if (firstShipment.agent && (!nextHeader.notify_party || nextHeader.notify_party_type === 'agent')) {
        nextHeader.notify_party_type = 'agent';
        nextHeader.notify_party = firstShipment.agent;
      }
    }

    // 3. Auto-prefill Technical Details
    let nextTechnicalDetails = { ...(payload.technical_details || {}) };
    const firstTechnical = selectedDockets.map(d => d.technical_details).find(t => t && Object.keys(t).length > 0);
    if (firstTechnical) {
      const techFieldsToCopy = [
        'temperature',
        'packaging',
        'recover_packing',
        'data_logger',
        'data_logger_supply_by',
        'logger_recover',
        'logger_reading',
        'dry_ice',
        'pickup_delivery_vehicle'
      ];
      techFieldsToCopy.forEach(k => {
        if (firstTechnical[k] !== undefined && firstTechnical[k] !== null && firstTechnical[k] !== '') {
          nextTechnicalDetails[k] = firstTechnical[k];
        }
      });
    }

    onChange({
      ...payload,
      header: nextHeader,
      shipment_info: nextShipmentInfo,
      technical_details: nextTechnicalDetails,
      consolidation: updatedConsolidation,
      package_info: {
        ...(payload.package_info || {}),
        package_no_of_packages: String(totalPkgs || ''),
        package_gross_weight: totalGw > 0 ? (Math.round(totalGw * 100) / 100).toFixed(2) : ''
      }
    });
  }, [payload, onChange]);

  const handleRemoveHouseDocket = useCallback((docketIdToRemove) => {
    const currentList = consolidation.house_dockets_summary || [];
    const updatedList = currentList.filter(d => d.id !== docketIdToRemove);
    const totalPkgs = updatedList.reduce((sum, d) => sum + parseNum(d.packages, false), 0);
    const totalGw = updatedList.reduce((sum, d) => sum + parseNum(d.gross_weight, true), 0);
    const linkedIds = updatedList.map(d => d.id);

    const updatedConsolidation = {
      is_consolidation: true,
      linked_house_docket_ids: linkedIds,
      house_dockets_summary: updatedList,
      total_house_dockets: updatedList.length,
      total_packages: totalPkgs,
      total_gross_weight: Math.round(totalGw * 100) / 100
    };

    onChange({
      ...payload,
      consolidation: updatedConsolidation,
      package_info: {
        ...(payload.package_info || {}),
        package_no_of_packages: String(totalPkgs || ''),
        package_gross_weight: totalGw > 0 ? (Math.round(totalGw * 100) / 100).toFixed(2) : ''
      }
    });
  }, [consolidation, payload, onChange]);

  // Live-computed package info — always derived from invoices, never edited manually
  const computedPackageInfo = useMemo(() => computePackageInfo(invoices), [invoices]);

  // When special case charges is active for any invoice, use airline_actual_weight
  // instead of cbm_chargeable_weight for that invoice's contribution to Package Info total
  const effectiveChargeableWeight = useMemo(() => {
    const total = invoices.reduce((sum, inv) => {
      const special = inv.special_case_charges && inv.airline_actual_weight;
      return sum + (parseFloat(special ? inv.airline_actual_weight : inv.cbm_chargeable_weight) || 0);
    }, 0);
    return total > 0 ? total.toFixed(2) : '';
  }, [invoices]);
  const technicalDetails = payload.technical_details || {};
  const tempLogger = payload.temperature_logger_block || {};
  const hazBlock = payload.haz_block || {};
  const deliveryInfo = payload.delivery_info || {};

  // Helper to get branches for a customer
  const getBranches = (customerId) => {
    if (!customerId || !customerDetails[customerId]) return [];
    return (customerDetails[customerId].branches || []).map(b => ({
      id: b.id,
      name: b.branch_nick_name || b.city || 'Branch',
      subtext: [b.city, b.state].filter(Boolean).join(', ')
    }));
  };

  // Transform agents for MasterCombobox
  const agentItems = agents.map(a => ({
    id: a.id,
    name: a.agent_name,
    subtext: [a.country, a.phone].filter(Boolean).join(' | ')
  }));

  const cargoType = header.cargo_type || '';
  // Visibility rules based on Cargo Type:
  // General/Valuable: Hide all conditional sections
  // HAZ/HAZ Temp: Show all sections
  // Non HAZ Temp/Perishable: Show Technical Details & Temp Logger, Hide HAZ block
  const showTechnicalDetails = ['HAZ', 'HAZ Temp', 'Non HAZ Temp', 'Perishable'].includes(cargoType);
  const showTempLogger = ['HAZ', 'HAZ Temp', 'Non HAZ Temp', 'Perishable'].includes(cargoType);
  const showHazBlock = ['HAZ', 'HAZ Temp'].includes(cargoType);

  // Auto-initialize international_domestic based on mode when value is not yet set
  // This fixes the bug where the print always shows "Domestic" for AIR/SEA schemas
  useEffect(() => {
    if (!header.international_domestic) {
      const defaultVal = mode === 'ROAD_RAIL' ? 'Domestic' : 'International';
      onChange({ ...payload, header: { ...header, international_domestic: defaultVal } });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Filter locations by mode (null = show all for ROAD_RAIL)
  const modeLocations = cfg.location_mode
    ? locations.filter(l => !l.mode_supported || (l.mode_supported || []).includes(cfg.location_mode))
    : locations;
  // Map locations for combobox display
  const locationItems = modeLocations.map(l => ({
    id: l.id,
    name: `${l.location_name}${l.location_code ? ' (' + l.location_code + ')' : ''}`,
    subtext: [l.city, l.country].filter(Boolean).join(', ')
  }));
  // Filter carriers by mode type
  const modeCarriers = carriers.filter(c => c.carrier_type === cfg.carrier_type);
  const carrierItems = modeCarriers.map(c => ({
    id: c.id,
    name: `${c.carrier_name}${c.carrier_code ? ' (' + c.carrier_code + ')' : ''}`,
    subtext: c.carrier_prefix_code ? `Prefix: ${c.carrier_prefix_code}` : c.country
  }));

  const updateSection = useCallback((section, field, value) => {
    onChange({
      ...payload,
      [section]: { ...(payload[section] || {}), [field]: value }
    });
  }, [payload, onChange]);

  // Handler function to update multiple header fields at once
  const updateHeaderFields = useCallback((updates) => {
    onChange({
      ...payload,
      header: { ...(payload.header || {}), ...updates }
    });
  }, [payload, onChange]);

  // Handler functions for customer selection with branch reset
  const handleBillToSelect = useCallback((customerId) => {
    updateHeaderFields({ bill_to: customerId, bill_to_branch: '' });
    if (customerId) onCustomerSelect(customerId, 'bill_to');
  }, [updateHeaderFields, onCustomerSelect]);

  const handleExporterSelect = useCallback((customerId) => {
    updateHeaderFields({ exporter_shipper: customerId, exporter_shipper_branch: '' });
    if (customerId) onCustomerSelect(customerId, 'exporter_shipper');
  }, [updateHeaderFields, onCustomerSelect]);

  const handleImporterSelect = useCallback((customerId) => {
    updateHeaderFields({ importer_receiver: customerId, importer_receiver_branch: '' });
    if (customerId) onCustomerSelect(customerId, 'importer_receiver');
  }, [updateHeaderFields, onCustomerSelect]);

  const handleNotifyPartySelect = useCallback((customerId) => {
    updateHeaderFields({ notify_party: customerId, notify_party_branch: '' });
    if (customerId) onCustomerSelect(customerId, 'notify_party');
  }, [updateHeaderFields, onCustomerSelect]);

  const handlePartyTypeChange = useCallback((fieldKey, newType) => {
    updateHeaderFields({
      [`${fieldKey}_type`]: newType,
      [fieldKey]: '',
      [`${fieldKey}_branch`]: ''
    });
  }, [updateHeaderFields]);

  const handleAgentSelect = useCallback((fieldKey, agentId) => {
    updateHeaderFields({
      [fieldKey]: agentId,
      [`${fieldKey}_branch`]: ''
    });
  }, [updateHeaderFields]);

  const updateScopeOfWork = useCallback((option, checked) => {
    const current = header.scope_of_work || [];
    const updated = checked ? [...current, option] : current.filter(s => s !== option);
    updateSection('header', 'scope_of_work', updated);
  }, [header.scope_of_work, updateSection]);

  // Functions for managing multiple invoices
  const updateInvoice = useCallback((invoiceIdx, field, value) => {
    const newInvoices = [...invoices];
    newInvoices[invoiceIdx] = { ...newInvoices[invoiceIdx], [field]: value };
    onChange({ ...payload, invoices: newInvoices, package_info: computePackageInfo(newInvoices) });
  }, [invoices, payload, onChange]);

  const addInvoice = useCallback(() => {
    const newInvoices = [...invoices, { ...EMPTY_INVOICE }];
    onChange({ ...payload, invoices: newInvoices, package_info: computePackageInfo(newInvoices) });
  }, [invoices, payload, onChange]);

  const removeInvoice = useCallback((idx) => {
    if (invoices.length <= 1) return;
    const newInvoices = invoices.filter((_, i) => i !== idx);
    onChange({ ...payload, invoices: newInvoices, package_info: computePackageInfo(newInvoices) });
  }, [invoices, payload, onChange]);

  const updateInvoiceDimension = useCallback((invoiceIdx, dimIdx, field, value) => {
    const newInvoices = [...invoices];
    const dims = [...(newInvoices[invoiceIdx].dimensions || [{ ...EMPTY_DIMENSION }])];
    const updatedDim = { ...dims[dimIdx], [field]: value };

    // Auto-calculate CBM (m³) based on dimension unit
    if (['length', 'width', 'height', 'no_of_packages'].includes(field)) {
      const unit = newInvoices[invoiceIdx].dimension_unit || 'CM';
      const l   = parseFloat(updatedDim.length)         || 0;
      const w   = parseFloat(updatedDim.width)          || 0;
      const h   = parseFloat(updatedDim.height)         || 0;
      const qty = parseFloat(updatedDim.no_of_packages) || 0;
      updatedDim.cbm_chargeable_weight = calcCBM(l, w, h, qty, unit);
    }

    dims[dimIdx] = updatedDim;
    const unit = newInvoices[invoiceIdx].dimension_unit || 'CM';
    const invoiceCbw = sumChargeableWeight(dims, unit, mode, payload.header?.international_domestic);
    newInvoices[invoiceIdx] = {
      ...newInvoices[invoiceIdx],
      dimensions: dims,
      cbm_chargeable_weight: invoiceCbw,
    };
    onChange({ ...payload, invoices: newInvoices, package_info: computePackageInfo(newInvoices) });
  }, [invoices, payload, onChange, mode]);

  // Recalculate all dimension CBMs when unit changes
  const updateDimensionUnit = useCallback((invoiceIdx, newUnit) => {
    const newInvoices = [...invoices];
    const inv = newInvoices[invoiceIdx];
    const dims = (inv.dimensions || []).map(dim => {
      const l   = parseFloat(dim.length)         || 0;
      const w   = parseFloat(dim.width)          || 0;
      const h   = parseFloat(dim.height)         || 0;
      const qty = parseFloat(dim.no_of_packages) || 0;
      return { ...dim, cbm_chargeable_weight: calcCBM(l, w, h, qty, newUnit) };
    });
    const invoiceCbw = sumChargeableWeight(dims, newUnit, mode, payload.header?.international_domestic);
    newInvoices[invoiceIdx] = {
      ...inv,
      dimension_unit: newUnit,
      dimensions: dims,
      cbm_chargeable_weight: invoiceCbw,
    };
    onChange({ ...payload, invoices: newInvoices, package_info: computePackageInfo(newInvoices) });
  }, [invoices, payload, onChange, mode]);

  const addInvoiceDimension = useCallback((invoiceIdx) => {
    const newInvoices = [...invoices];
    const dims = [...(newInvoices[invoiceIdx].dimensions || [])];
    dims.push({ ...EMPTY_DIMENSION });
    newInvoices[invoiceIdx] = { ...newInvoices[invoiceIdx], dimensions: dims };
    onChange({ ...payload, invoices: newInvoices });
  }, [invoices, payload, onChange]);

  const removeInvoiceDimension = useCallback((invoiceIdx, dimIdx) => {
    const newInvoices = [...invoices];
    const dims = newInvoices[invoiceIdx].dimensions || [];
    if (dims.length <= 1) return;
    newInvoices[invoiceIdx] = { ...newInvoices[invoiceIdx], dimensions: dims.filter((_, i) => i !== dimIdx) };
    onChange({ ...payload, invoices: newInvoices });
  }, [invoices, payload, onChange]);

  // ── Container detail handlers (SEA only) ────────────────────────────────────
  const containerDetails = useMemo(() => payload.container_details || [], [payload.container_details]);
  const displayContainers = containerDetails.length > 0 ? containerDetails : [{ ...EMPTY_CONTAINER }];

  const updateContainer = useCallback((idx, field, value) => {
    const updated = containerDetails.map((r, i) => i === idx ? { ...r, [field]: value } : r);
    onChange({ ...payload, container_details: updated });
  }, [containerDetails, payload, onChange]);

  const addContainer = useCallback(() => {
    onChange({ ...payload, container_details: [...containerDetails, { ...EMPTY_CONTAINER }] });
  }, [containerDetails, payload, onChange]);

  const removeContainer = useCallback((idx) => {
    if (containerDetails.length <= 1) return;
    onChange({ ...payload, container_details: containerDetails.filter((_, i) => i !== idx) });
  }, [containerDetails, payload, onChange]);

  // ── Sticky Tab & Accordion Navigation State ──
  const [collapsedSections, setCollapsedSections] = useState({
    'sec-header': false,
    'sec-consolidation': false,
    'sec-invoices': true,
    'sec-containers': true,
    'sec-package': true,
    'sec-shipment': true,
    'sec-technical': true,
    'sec-temp': true,
    'sec-haz': true,
    'sec-delivery': true,
  });
  const [activeSection, setActiveSection] = useState('sec-header');

  const toggleSectionCollapse = useCallback((id) => {
    setCollapsedSections(prev => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const toggleAllSections = useCallback(() => {
    setCollapsedSections(prev => {
      const isAnyCollapsed = Object.values(prev).filter(Boolean).length > 0;
      if (isAnyCollapsed) {
        return {}; // Expand all
      } else {
        return {
          'sec-header': false,
          'sec-consolidation': false,
          'sec-invoices': false,
          'sec-containers': false,
          'sec-package': false,
          'sec-shipment': true,
          'sec-technical': true,
          'sec-temp': true,
          'sec-haz': true,
          'sec-delivery': true,
        };
      }
    });
  }, []);

  const activeTabs = useMemo(() => {
    const tabs = [
      { id: 'sec-header', label: 'Job & Parties', icon: ClipboardList },
    ];
    if (isConsolidation) {
      tabs.push({ id: 'sec-consolidation', label: 'House Dockets', icon: Layers, count: linkedHouseDockets.length });
    }
    tabs.push({ id: 'sec-invoices', label: 'Invoices', icon: FileText, count: invoices.length });
    if (mode === 'ROAD_RAIL' || mode === 'AIR_V2') {
      tabs.push({ id: 'sec-delivery', label: 'Delivery', icon: Truck });
    }
    if (mode === 'SEA') {
      tabs.push({ id: 'sec-containers', label: 'Containers', icon: Ship, count: containerDetails.length });
    }
    tabs.push({ id: 'sec-package', label: 'Package Info', icon: Package });
    if (mode !== 'ROAD_RAIL') {
      tabs.push({
        id: 'sec-shipment',
        label: mode === 'SEA' ? 'Voyage & Port' : 'Flight & Routing',
        icon: mode === 'SEA' ? Ship : Plane,
      });
    }
    if (showTechnicalDetails) {
      tabs.push({ id: 'sec-technical', label: 'Technical', icon: Settings2 });
    }
    if (showTempLogger) {
      tabs.push({ id: 'sec-temp', label: 'Cold Chain', icon: Thermometer });
    }
    if (showHazBlock) {
      tabs.push({ id: 'sec-haz', label: 'HAZ Cargo', icon: AlertTriangle });
    }
    return tabs;
  }, [isConsolidation, linkedHouseDockets.length, invoices.length, mode, containerDetails.length, showTechnicalDetails, showTempLogger, showHazBlock]);

  // Scrollspy: update active tab when scrolling
  useEffect(() => {
    const sectionIds = activeTabs.map(t => t.id);
    const handleScroll = () => {
      const scrollPos = window.scrollY + 200;
      for (let i = sectionIds.length - 1; i >= 0; i--) {
        const el = document.getElementById(sectionIds[i]);
        if (el && el.offsetTop <= scrollPos) {
          setActiveSection(sectionIds[i]);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [activeTabs]);

  const scrollToSection = useCallback((id) => {
    // Expand section if collapsed
    setCollapsedSections(prev => prev[id] ? { ...prev, [id]: false } : prev);
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setActiveSection(id);
      }
    }, 50);
  }, []);



  return (
    <div className="flex flex-col gap-5">
      {/* ── Sticky Scroll-Tabs Navigation Bar ── */}
      <div className="sticky top-[68px] z-30 bg-white/95 backdrop-blur-md border border-gray-200/90 rounded-xl px-2.5 py-2 shadow-xs flex items-center justify-between gap-2 overflow-x-auto transition-all">
        <div className="flex items-center gap-1.5 min-w-max">
          {activeTabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeSection === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => scrollToSection(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold font-ibm transition-all select-none ${
                  isActive
                    ? 'bg-blue-600 text-white shadow-xs'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100/90'
                }`}
              >
                <Icon className={`w-3.5 h-3.5 ${isActive ? 'text-white' : 'text-gray-400'}`} />
                <span>{tab.label}</span>
                {tab.count !== undefined && (
                  <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold ${
                    isActive ? 'bg-white/25 text-white' : 'bg-gray-200 text-gray-700'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Right utility: Toggle Sections */}
        <div className="hidden md:flex items-center gap-2 pr-1.5 shrink-0 border-l border-gray-200 pl-3">
          <button
            type="button"
            onClick={toggleAllSections}
            className="text-[11px] font-medium text-gray-500 hover:text-blue-600 flex items-center gap-1 transition-colors select-none"
            title="Toggle compact views"
          >
            <Layers className="w-3.5 h-3.5 text-gray-400" />
            <span>Compact View</span>
          </button>
        </div>
      </div>
      {/* SECTION 1 — BASIC JOB / HEADER */}
      <Card id="sec-header" className="scroll-mt-32 bg-white rounded-xl border border-gray-200/90 shadow-xs hover:border-gray-300 transition-all overflow-hidden">
        <div 
          className={`px-6 py-4 flex items-center justify-between cursor-pointer select-none transition-colors ${
            collapsedSections['sec-header'] ? 'bg-sky-50/30 hover:bg-sky-50/60 border-l-4 border-l-sky-500' : 'border-b-4 border-sky-500'
          }`}
          onClick={() => toggleSectionCollapse('sec-header')}
        >
          <div className="flex items-center gap-3">
            <ClipboardList className="w-5 h-5 text-sky-500" />
            <h2 data-testid="air-header-section" className="text-lg font-semibold text-text-dark font-manrope">JOB INFORMATION</h2>
          </div>
          <div className="flex items-center gap-3">
            {collapsedSections['sec-header'] && (
              <span className="text-xs font-ibm text-gray-500 bg-sky-50 border border-sky-200 px-2.5 py-0.5 rounded-full">
                {header.job_no || 'Job'} · {exporterDetails?.customer_name || 'Shipper'} ➔ {importerDetails?.customer_name || 'Consignee'}
              </span>
            )}
            <button type="button" className="text-gray-400 hover:text-gray-600 p-1">
              {collapsedSections['sec-header'] ? <ChevronDown className="w-5 h-5" /> : <ChevronUp className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {!collapsedSections['sec-header'] && (
        <div className="p-6 space-y-6">
          {/* Row 1: 6 fields */}
          <div className={`grid grid-cols-2 md:grid-cols-3 gap-4 ${isAirV2 ? 'lg:grid-cols-5' : 'lg:grid-cols-6'}`}>
            {!isAirV2 && (
            <div className="space-y-1.5">
              <Label className="text-text-dark font-ibm font-medium text-xs">International / Domestic <span className="text-red-500">*</span></Label>
              <Select value={header.international_domestic || 'International'} onValueChange={v => updateSection('header', 'international_domestic', v)}>
                <SelectTrigger data-testid="international-domestic-select" className="h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="International">International</SelectItem>
                  <SelectItem value="Domestic">Domestic</SelectItem>
                </SelectContent>
              </Select>
            </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-text-dark font-ibm font-medium text-xs">Mode Of Transport <span className="text-red-500">*</span></Label>
              <Select value={cfg.mode_label} disabled>
                <SelectTrigger data-testid="mode-transport-select" className="h-9 text-sm bg-gray-50"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value={cfg.mode_label}>{cfg.mode_label}</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-text-dark font-ibm font-medium text-xs">Inward / Outward <span className="text-red-500">*</span></Label>
              <Select value={header.inward_outward || ''} onValueChange={v => updateSection('header', 'inward_outward', v)}>
                <SelectTrigger data-testid="inward-outward-select" className="h-9 text-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Inward">Inward</SelectItem>
                  <SelectItem value="Outward">Outward</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-text-dark font-ibm font-medium text-xs">Service <span className="text-red-500">*</span></Label>
              <Select value={header.service_type || ''} onValueChange={v => updateSection('header', 'service_type', v)}>
                <SelectTrigger data-testid="service-type-select" className="h-9 text-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {['Services', 'Door To Door', 'Door To Port', 'Port To Port', 'Port To Door'].map(o => (
                    <SelectItem key={o} value={o}>{o}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-text-dark font-ibm font-medium text-xs">Cargo Type <span className="text-red-500">*</span></Label>
              <Select value={header.cargo_type || ''} onValueChange={v => updateSection('header', 'cargo_type', v)}>
                <SelectTrigger data-testid="cargo-type-select" className="h-9 text-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {['General', 'HAZ', 'HAZ Temp', 'Non HAZ Temp', 'Perishable', 'Valuable'].map(o => (
                    <SelectItem key={o} value={o}>{o}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-text-dark font-ibm font-medium text-xs">Commercial Type</Label>
              <Select value={header.commercial_type || ''} onValueChange={v => updateSection('header', 'commercial_type', v)}>
                <SelectTrigger data-testid="commercial-type-select" className="h-9 text-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Commercial">Commercial</SelectItem>
                  <SelectItem value="Non Commercial">Non Commercial</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Row 2: Customer / Agent selectors */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Bill To */}
            <div className="p-3 rounded-lg border border-gray-200/80 bg-gray-50/40 space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-text-dark font-ibm font-medium text-xs">
                  Accounting Info / Bill To <span className="text-red-500">*</span>
                </Label>
                <div className="inline-flex items-center p-0.5 bg-gray-200/70 rounded-md text-[10px] font-ibm">
                  <button
                    type="button"
                    onClick={() => handlePartyTypeChange('bill_to', 'customer')}
                    className={`px-2 py-0.5 rounded transition-all ${billToType === 'customer' ? 'bg-white shadow-xs text-blue-700 font-bold' : 'text-gray-500 hover:text-gray-800'}`}
                    data-testid="bill-to-type-customer"
                  >
                    Customer
                  </button>
                  <button
                    type="button"
                    onClick={() => handlePartyTypeChange('bill_to', 'agent')}
                    className={`px-2 py-0.5 rounded transition-all ${billToType === 'agent' ? 'bg-violet-600 shadow-xs text-white font-bold' : 'text-gray-500 hover:text-gray-800'}`}
                    data-testid="bill-to-type-agent"
                  >
                    Agent
                  </button>
                </div>
              </div>

              {billToType === 'customer' ? (
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <CustomerCombobox customers={customers} value={header.bill_to} onSelect={handleBillToSelect} testId="bill-to-select" placeholder="Select Customer" />
                  </div>
                  <div className="space-y-1">
                    <MasterCombobox 
                      items={getBranches(header.bill_to)} 
                      value={header.bill_to_branch} 
                      onSelect={v => updateSection('header', 'bill_to_branch', v)} 
                      testId="bill-to-branch-select" 
                      placeholder={header.bill_to ? "Select Branch" : "Select customer first"} 
                      displayField="name" 
                      subtextField="subtext" 
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-1">
                  <MasterCombobox
                    items={agentItems}
                    value={header.bill_to}
                    onSelect={v => handleAgentSelect('bill_to', v)}
                    testId="bill-to-agent-select"
                    placeholder="Select Agent (from Agent Master)"
                    displayField="name"
                    subtextField="subtext"
                  />
                  {header.bill_to && agentMap[header.bill_to] && (
                    <p className="text-[11px] text-gray-500 font-ibm px-1 truncate">
                      <span className="font-semibold text-violet-700">Agent: </span>
                      {[agentMap[header.bill_to].country, agentMap[header.bill_to].phone, agentMap[header.bill_to].email].filter(Boolean).join(' • ')}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Exporter/Shipper */}
            <div className="p-3 rounded-lg border border-gray-200/80 bg-gray-50/40 space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-text-dark font-ibm font-medium text-xs">
                  Exporter / Shipper <span className="text-red-500">*</span>
                </Label>
                <div className="inline-flex items-center p-0.5 bg-gray-200/70 rounded-md text-[10px] font-ibm">
                  <button
                    type="button"
                    onClick={() => handlePartyTypeChange('exporter_shipper', 'customer')}
                    className={`px-2 py-0.5 rounded transition-all ${exporterType === 'customer' ? 'bg-white shadow-xs text-blue-700 font-bold' : 'text-gray-500 hover:text-gray-800'}`}
                    data-testid="exporter-type-customer"
                  >
                    Customer
                  </button>
                  <button
                    type="button"
                    onClick={() => handlePartyTypeChange('exporter_shipper', 'agent')}
                    className={`px-2 py-0.5 rounded transition-all ${exporterType === 'agent' ? 'bg-violet-600 shadow-xs text-white font-bold' : 'text-gray-500 hover:text-gray-800'}`}
                    data-testid="exporter-type-agent"
                  >
                    Agent
                  </button>
                </div>
              </div>

              {exporterType === 'customer' ? (
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <CustomerCombobox customers={customers} value={header.exporter_shipper} onSelect={handleExporterSelect} testId="exporter-shipper-select" placeholder="Select Exporter/Shipper" />
                  </div>
                  <div className="space-y-1">
                    <MasterCombobox 
                      items={getBranches(header.exporter_shipper)} 
                      value={header.exporter_shipper_branch} 
                      onSelect={v => updateSection('header', 'exporter_shipper_branch', v)} 
                      testId="exporter-shipper-branch-select" 
                      placeholder={header.exporter_shipper ? "Select Branch" : "Select customer first"} 
                      displayField="name" 
                      subtextField="subtext" 
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-1">
                  <MasterCombobox
                    items={agentItems}
                    value={header.exporter_shipper}
                    onSelect={v => handleAgentSelect('exporter_shipper', v)}
                    testId="exporter-agent-select"
                    placeholder="Select Agent (from Agent Master)"
                    displayField="name"
                    subtextField="subtext"
                  />
                  {header.exporter_shipper && agentMap[header.exporter_shipper] && (
                    <p className="text-[11px] text-gray-500 font-ibm px-1 truncate">
                      <span className="font-semibold text-violet-700">Agent: </span>
                      {[agentMap[header.exporter_shipper].country, agentMap[header.exporter_shipper].phone, agentMap[header.exporter_shipper].email].filter(Boolean).join(' • ')}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Row 3: Importer/Receiver & Notify Party */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Importer/Receiver */}
            <div className="p-3 rounded-lg border border-gray-200/80 bg-gray-50/40 space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-text-dark font-ibm font-medium text-xs">
                  {isConsolidation ? 'Destination Agent / Importer' : 'Importer / Receiver'} <span className="text-red-500">*</span>
                </Label>
                <div className="inline-flex items-center p-0.5 bg-gray-200/70 rounded-md text-[10px] font-ibm">
                  <button
                    type="button"
                    onClick={() => handlePartyTypeChange('importer_receiver', 'customer')}
                    className={`px-2 py-0.5 rounded transition-all ${importerType === 'customer' ? 'bg-white shadow-xs text-blue-700 font-bold' : 'text-gray-500 hover:text-gray-800'}`}
                    data-testid="importer-type-customer"
                  >
                    Customer
                  </button>
                  <button
                    type="button"
                    onClick={() => handlePartyTypeChange('importer_receiver', 'agent')}
                    className={`px-2 py-0.5 rounded transition-all ${importerType === 'agent' ? 'bg-violet-600 shadow-xs text-white font-bold' : 'text-gray-500 hover:text-gray-800'}`}
                    data-testid="importer-type-agent"
                  >
                    Agent
                  </button>
                </div>
              </div>

              {importerType === 'customer' ? (
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <CustomerCombobox customers={customers} value={header.importer_receiver} onSelect={handleImporterSelect} testId="importer-receiver-select" placeholder="Select Importer/Receiver" />
                  </div>
                  <div className="space-y-1">
                    <MasterCombobox 
                      items={getBranches(header.importer_receiver)} 
                      value={header.importer_receiver_branch} 
                      onSelect={v => updateSection('header', 'importer_receiver_branch', v)} 
                      testId="importer-receiver-branch-select" 
                      placeholder={header.importer_receiver ? "Select Branch" : "Select customer first"} 
                      displayField="name" 
                      subtextField="subtext" 
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-1">
                  <MasterCombobox
                    items={agentItems}
                    value={header.importer_receiver}
                    onSelect={v => handleAgentSelect('importer_receiver', v)}
                    testId="importer-agent-select"
                    placeholder="Select Destination Agent (from Agent Master)"
                    displayField="name"
                    subtextField="subtext"
                  />
                  {header.importer_receiver && agentMap[header.importer_receiver] && (
                    <p className="text-[11px] text-gray-500 font-ibm px-1 truncate">
                      <span className="font-semibold text-violet-700">Agent: </span>
                      {[agentMap[header.importer_receiver].country, agentMap[header.importer_receiver].phone, agentMap[header.importer_receiver].email].filter(Boolean).join(' • ')}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Notify Party */}
            {!isAirV2 && (
            <div className="p-3 rounded-lg border border-gray-200/80 bg-gray-50/40 space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-text-dark font-ibm font-medium text-xs">Notify Party</Label>
                <div className="inline-flex items-center p-0.5 bg-gray-200/70 rounded-md text-[10px] font-ibm">
                  <button
                    type="button"
                    onClick={() => handlePartyTypeChange('notify_party', 'customer')}
                    className={`px-2 py-0.5 rounded transition-all ${notifyType === 'customer' ? 'bg-white shadow-xs text-blue-700 font-bold' : 'text-gray-500 hover:text-gray-800'}`}
                    data-testid="notify-type-customer"
                  >
                    Customer
                  </button>
                  <button
                    type="button"
                    onClick={() => handlePartyTypeChange('notify_party', 'agent')}
                    className={`px-2 py-0.5 rounded transition-all ${notifyType === 'agent' ? 'bg-violet-600 shadow-xs text-white font-bold' : 'text-gray-500 hover:text-gray-800'}`}
                    data-testid="notify-type-agent"
                  >
                    Agent
                  </button>
                </div>
              </div>

              {notifyType === 'customer' ? (
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <CustomerCombobox customers={customers} value={header.notify_party} onSelect={handleNotifyPartySelect} testId="notify-party-select" placeholder="Select Notify Party" />
                  </div>
                  <div className="space-y-1">
                    <MasterCombobox 
                      items={getBranches(header.notify_party)} 
                      value={header.notify_party_branch} 
                      onSelect={v => updateSection('header', 'notify_party_branch', v)} 
                      testId="notify-party-branch-select" 
                      placeholder={header.notify_party ? "Select Branch" : "Select customer first"} 
                      displayField="name" 
                      subtextField="subtext" 
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-1">
                  <MasterCombobox
                    items={agentItems}
                    value={header.notify_party}
                    onSelect={v => handleAgentSelect('notify_party', v)}
                    testId="notify-agent-select"
                    placeholder="Select Notify Agent (from Agent Master)"
                    displayField="name"
                    subtextField="subtext"
                  />
                  {header.notify_party && agentMap[header.notify_party] && (
                    <p className="text-[11px] text-gray-500 font-ibm px-1 truncate">
                      <span className="font-semibold text-violet-700">Agent: </span>
                      {[agentMap[header.notify_party].country, agentMap[header.notify_party].phone, agentMap[header.notify_party].email].filter(Boolean).join(' • ')}
                    </p>
                  )}
                </div>
              )}
            </div>
            )}
          </div>

          {/* Row 4: Consolidation + Scope */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-text-dark font-ibm font-medium text-xs">{isSea ? 'Sea Shipment Structure (MBL / HBL)' : 'Air Shipment Structure (MAWB / HAWB)'}</Label>
                <span className="text-[10px] font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded border border-blue-200">
                  Select Structure
                </span>
              </div>
              <RadioGroup
                value={shipmentStructure}
                onValueChange={(v) => {
                  if (v === 'CONSOLIDATION') {
                    onChange({
                      ...payload,
                      header: {
                        ...(payload.header || {}),
                        shipment_structure: 'CONSOLIDATION',
                        consolidation_job: 'Yes',
                        importer_receiver_type: payload.header?.importer_receiver_type || 'agent',
                        notify_party_type: payload.header?.notify_party_type || 'agent'
                      },
                      consolidation: { ...(payload.consolidation || {}), is_consolidation: true }
                    });
                  } else if (v === 'DIRECT') {
                    onChange({
                      ...payload,
                      header: {
                        ...(payload.header || {}),
                        shipment_structure: 'DIRECT',
                        consolidation_job: 'No',
                        importer_receiver_type: payload.header?.importer_receiver_type === 'agent' && !payload.header?.importer_receiver ? 'customer' : (payload.header?.importer_receiver_type || 'customer'),
                        notify_party_type: payload.header?.notify_party_type === 'agent' && !payload.header?.notify_party ? 'customer' : (payload.header?.notify_party_type || 'customer')
                      },
                      consolidation: { is_consolidation: false, linked_house_docket_ids: [], house_dockets_summary: [] }
                    });
                  } else {
                    // BACK_TO_BACK
                    onChange({
                      ...payload,
                      header: {
                        ...(payload.header || {}),
                        shipment_structure: 'BACK_TO_BACK',
                        consolidation_job: 'No',
                        importer_receiver_type: payload.header?.importer_receiver_type === 'agent' && !payload.header?.importer_receiver ? 'customer' : (payload.header?.importer_receiver_type || 'customer'),
                        notify_party_type: payload.header?.notify_party_type === 'agent' && !payload.header?.notify_party ? 'customer' : (payload.header?.notify_party_type || 'customer')
                      },
                      consolidation: { is_consolidation: false, linked_house_docket_ids: [], house_dockets_summary: [] }
                    });
                  }
                }}
                className="flex flex-wrap gap-4 pt-1"
                data-testid="consolidation-job-radio"
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="DIRECT" id="structure-direct" />
                  <Label htmlFor="structure-direct" className="text-sm font-ibm cursor-pointer font-medium text-blue-700">
                    {isSea ? 'Direct Shipment (MBL Only)' : 'Direct Shipment (MAWB Only)'}
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="BACK_TO_BACK" id="structure-b2b" />
                  <Label htmlFor="structure-b2b" className="text-sm font-ibm cursor-pointer font-medium text-emerald-700">
                    {isSea ? 'Back-to-Back (1 HBL + 1 MBL)' : 'Back-to-Back (1 HAWB + 1 MAWB)'}
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="CONSOLIDATION" id="structure-consol" />
                  <Label htmlFor="structure-consol" className="text-sm font-ibm cursor-pointer font-medium text-violet-700">
                    {isSea ? 'Consolidation (Multiple HBLs + 1 MBL)' : 'Consolidation (Multiple HAWBs + 1 MAWB)'}
                  </Label>
                </div>
              </RadioGroup>
            </div>
            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium text-xs">Scope of Work</Label>
              <div className="flex flex-wrap gap-x-4 gap-y-2" data-testid="scope-of-work-group">
                {SCOPE_OF_WORK_OPTIONS.map(opt => (
                  <div key={opt} className="flex items-center gap-1.5">
                    <Checkbox
                      id={`scope-${opt}`}
                      checked={(header.scope_of_work || []).includes(opt)}
                      onCheckedChange={checked => updateScopeOfWork(opt, checked)}
                    />
                    <Label htmlFor={`scope-${opt}`} className="text-xs font-ibm cursor-pointer">{opt}</Label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        )}
      </Card>

      {/* SECTION 1b — ATTACHED HOUSE DOCKETS FOR MASTER CONSOLIDATION */}
      {isConsolidation && (
        <Card id="sec-consolidation" className="scroll-mt-32 bg-white rounded-xl border border-violet-200 shadow-sm overflow-hidden" data-testid="attached-house-dockets-card">
          <div className="border-b-4 border-violet-600 px-6 py-4 bg-violet-50/50 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-violet-600 text-white rounded-lg shadow-sm">
                <Layers className="w-5 h-5" />
              </div>
              <div>
                <h2 data-testid="consolidation-house-section" className="text-lg font-bold text-gray-900 font-manrope">
                  {isSea ? 'CONSOLIDATED HOUSE DOCKETS (HBL)' : 'CONSOLIDATED HOUSE DOCKETS (HAWB)'}
                </h2>
                <p className="text-xs text-violet-700 font-ibm">
                  House shipments consolidated under this Master Docket. Packages and Gross Weight auto-aggregate into this Master.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                type="button"
                size="sm"
                onClick={() => setIsPickerOpen(true)}
                className="h-8 bg-violet-600 hover:bg-violet-700 text-white font-medium font-ibm text-xs shadow-sm"
                data-testid="open-house-picker-btn"
              >
                <Plus className="w-4 h-4 mr-1.5" />
                {linkedHouseDockets.length > 0 ? `Manage House Dockets (${linkedHouseDockets.length})` : 'Select / Attach House Dockets'}
              </Button>
            </div>
          </div>

          <div className="p-6 space-y-5">
            {/* Metric Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-violet-50/60 border border-violet-100 rounded-lg p-3">
                <span className="text-[11px] font-semibold text-violet-600 uppercase tracking-wider block font-ibm">
                  Attached House Dockets
                </span>
                <span className="text-2xl font-bold text-violet-900 font-manrope">
                  {linkedHouseDockets.length}
                </span>
              </div>
              <div className="bg-emerald-50/60 border border-emerald-100 rounded-lg p-3">
                <span className="text-[11px] font-semibold text-emerald-600 uppercase tracking-wider block font-ibm">
                  Total Combined Packages
                </span>
                <span className="text-2xl font-bold text-emerald-900 font-manrope">
                  {consolidation.total_packages || consolTotalPkgs}
                </span>
              </div>
              <div className="bg-amber-50/60 border border-amber-100 rounded-lg p-3">
                <span className="text-[11px] font-semibold text-amber-600 uppercase tracking-wider block font-ibm">
                  Total Combined Gross Weight
                </span>
                <span className="text-2xl font-bold text-amber-900 font-manrope">
                  {(consolTotalGw > 0 ? consolTotalGw : parseNum(consolidation.total_gross_weight, true)).toFixed(2)} kg
                </span>
              </div>
            </div>

            {/* Table of Attached House Dockets */}
            {linkedHouseDockets.length > 0 ? (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full text-xs text-left">
                  <thead className="bg-gray-50/90 text-gray-600 uppercase text-[11px] font-semibold border-b border-gray-200">
                    <tr>
                      <th className="p-3">Docket No</th>
                      <th className="p-3">{isSea ? 'HBL No' : 'HAWB No'}</th>
                      <th className="p-3">Shipper (Consignor)</th>
                      <th className="p-3">Consignee</th>
                      <th className="p-3 text-right">Packages</th>
                      <th className="p-3 text-right">Gross Wt (kg)</th>
                      <th className="p-3">Destination</th>
                      <th className="p-3 text-center w-16">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 font-ibm">
                    {linkedHouseDockets.map((d) => (
                      <tr key={d.id} className="hover:bg-violet-50/30 transition-colors">
                        <td className="p-3 font-semibold text-gray-900">
                          {d.system_docket_no}
                          {d.printed_docket_no ? (
                            <span className="block text-[10px] text-gray-400 font-normal">
                              Book: {d.printed_docket_no}
                            </span>
                          ) : null}
                        </td>
                        <td className="p-3">
                          <span className="inline-block px-2 py-0.5 rounded bg-violet-100 text-violet-700 font-semibold text-[11px]">
                            {d.hawb_no || '-'}
                          </span>
                        </td>
                        <td className="p-3 text-gray-700 max-w-[150px] truncate" title={d.shipper_name}>
                          {d.shipper_name || '-'}
                        </td>
                        <td className="p-3 text-gray-700 max-w-[150px] truncate" title={d.consignee_name}>
                          {d.consignee_name || '-'}
                        </td>
                        <td className="p-3 text-right font-semibold text-gray-900">{parseNum(d.packages, false) || 0}</td>
                        <td className="p-3 text-right font-semibold text-gray-900">{parseNum(d.gross_weight, true).toFixed(2)}</td>
                        <td className="p-3 text-gray-600">{d.destination || '-'}</td>
                        <td className="p-3 text-center">
                          <button
                            type="button"
                            onClick={() => handleRemoveHouseDocket(d.id)}
                            className="p-1 rounded text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                            title="Remove from consolidation"
                            data-testid={`remove-house-docket-${d.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg bg-gray-50/50">
                {isSea ? <Ship className="w-8 h-8 text-gray-300 mx-auto mb-2" /> : <Plane className="w-8 h-8 text-gray-300 mx-auto mb-2" />}
                <p className="text-xs font-medium text-gray-600">No House Dockets Attached Yet</p>
                <p className="text-[11px] text-gray-400 mt-1 max-w-md mx-auto">
                  {`Click "Select / Attach House Dockets" to attach eligible unconsolidated ${isSea ? 'SEA' : 'AIR'} shipments to this Master docket.`}
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setIsPickerOpen(true)}
                  className="mt-3 text-xs border-violet-300 text-violet-700 hover:bg-violet-50 font-ibm"
                >
                  <Plus className="w-3.5 h-3.5 mr-1" /> Select House Dockets
                </Button>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* SECTION 2 — INVOICE INFO (Multiple) */}
      <Card id="sec-invoices" className="scroll-mt-32 bg-white rounded-xl border border-gray-200/90 shadow-xs hover:border-gray-300 transition-all overflow-hidden">
        <div 
          className={`px-6 py-4 flex items-center justify-between cursor-pointer select-none transition-colors ${
            collapsedSections['sec-invoices'] ? 'bg-emerald-50/30 hover:bg-emerald-50/60 border-l-4 border-l-emerald-500' : 'border-b-4 border-emerald-500'
          }`}
          onClick={() => toggleSectionCollapse('sec-invoices')}
        >
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-emerald-600" />
            <h2 data-testid="air-invoice-section" className="text-lg font-semibold text-text-dark font-manrope">INVOICE INFO ({invoices.length})</h2>
          </div>
          <div className="flex items-center gap-3">
            {collapsedSections['sec-invoices'] && (
              <span className="text-xs font-ibm text-emerald-900 bg-emerald-50 border border-emerald-200 px-3 py-1 rounded-full flex items-center gap-1.5 shadow-2xs">
                <span className="font-semibold">{invoices.length} Invoice(s)</span>
                {invoices[0]?.invoice_no && <span className="text-emerald-700">· #{invoices[0].invoice_no}</span>}
                {invoices[0]?.invoice_value && <span className="text-emerald-800">· {invoices[0].invoice_currency || 'INR'} {invoices[0].invoice_value}</span>}
              </span>
            )}
            <Button 
              type="button" 
              size="sm" 
              onClick={(e) => { e.stopPropagation(); addInvoice(); }} 
              className="h-8 bg-emerald-500 hover:bg-emerald-600 text-white" 
              data-testid="add-invoice-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Add Invoice
            </Button>
            <button type="button" className="text-gray-400 hover:text-gray-600 p-1">
              {collapsedSections['sec-invoices'] ? <ChevronDown className="w-5 h-5" /> : <ChevronUp className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {!collapsedSections['sec-invoices'] && (
        <div className="p-6 space-y-6">
          {invoices.map((inv, invIdx) => (
            <div key={invIdx} className="relative border border-gray-200 rounded-lg p-4 space-y-4">
              {invoices.length > 1 && (
                <Button 
                  type="button" 
                  size="icon" 
                  variant="ghost" 
                  onClick={() => removeInvoice(invIdx)} 
                  className="absolute top-2 right-2 h-7 w-7 text-red-400 hover:text-red-600 hover:bg-red-50"
                  data-testid={`remove-invoice-${invIdx}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
              
              {/* Invoice Header Row: Commercial & Customs Reference Fields */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {/* 1. Invoice No. */}
                <div className="space-y-1.5">
                  <Label className="text-text-dark font-ibm font-medium text-xs">Invoice No.</Label>
                  <Input 
                    type="text" 
                    data-testid={`inv-${invIdx}-invoice_no`} 
                    value={inv.invoice_no || ''} 
                    onChange={e => updateInvoice(invIdx, 'invoice_no', e.target.value)} 
                    className="h-9 text-sm border-gray-200" 
                    placeholder="Invoice No." 
                  />
                </div>

                {/* 2. Invoice Value (Currency + Amount) */}
                <div className="space-y-1.5">
                  <Label className="text-text-dark font-ibm font-medium text-xs">Invoice Value</Label>
                  <div className="flex gap-1">
                    <select
                      data-testid={`inv-${invIdx}-invoice_currency`}
                      value={inv.invoice_currency || 'INR'}
                      onChange={e => updateInvoice(invIdx, 'invoice_currency', e.target.value)}
                      className="h-9 text-xs border border-gray-200 rounded-md px-1.5 font-ibm bg-white text-text-dark shrink-0 w-[68px] focus:outline-none focus:ring-1 focus:ring-emerald-400"
                    >
                      {INVOICE_CURRENCIES.map(c => (
                        <option key={c.value} value={c.value}>{c.value}</option>
                      ))}
                    </select>
                    <Input
                      type="text"
                      data-testid={`inv-${invIdx}-invoice_value`}
                      value={inv.invoice_value || ''}
                      onChange={e => updateInvoice(invIdx, 'invoice_value', e.target.value)}
                      className="h-9 text-sm border-gray-200 min-w-0 flex-1"
                      placeholder="Invoice Value"
                    />
                  </div>
                </div>

                {/* 3. Invoice Date */}
                <div className="space-y-1.5">
                  <Label className="text-text-dark font-ibm font-medium text-xs">Invoice Date</Label>
                  <Input 
                    type="date" 
                    data-testid={`inv-${invIdx}-invoice_date`} 
                    value={inv.invoice_date || ''} 
                    onChange={e => updateInvoice(invIdx, 'invoice_date', e.target.value)} 
                    className="h-9 text-sm border-gray-200" 
                    placeholder="Invoice Date" 
                  />
                </div>

                {/* 4. SB / BE No. */}
                <div className="space-y-1.5">
                  <Label className="text-text-dark font-ibm font-medium text-xs">SB / BE No.</Label>
                  <Input 
                    type="text" 
                    data-testid={`inv-${invIdx}-sb_be_no`} 
                    value={inv.sb_be_no || ''} 
                    onChange={e => updateInvoice(invIdx, 'sb_be_no', e.target.value)} 
                    className="h-9 text-sm border-gray-200" 
                    placeholder="SB / BE No." 
                  />
                </div>

                {/* 5. SB / BE Date */}
                <div className="space-y-1.5">
                  <Label className="text-text-dark font-ibm font-medium text-xs">SB / BE Date</Label>
                  <Input 
                    type="date" 
                    data-testid={`inv-${invIdx}-second_date`} 
                    value={inv.second_date || ''} 
                    onChange={e => updateInvoice(invIdx, 'second_date', e.target.value)} 
                    className="h-9 text-sm border-gray-200" 
                    placeholder="SB / BE Date" 
                  />
                </div>

                {/* 6. P/O No. */}
                <div className="space-y-1.5">
                  <Label className="text-text-dark font-ibm font-medium text-xs">P/O No.</Label>
                  <Input 
                    type="text" 
                    data-testid={`inv-${invIdx}-po_no`} 
                    value={inv.po_no || ''} 
                    onChange={e => updateInvoice(invIdx, 'po_no', e.target.value)} 
                    className="h-9 text-sm border-gray-200" 
                    placeholder="P/O No." 
                  />
                </div>
              </div>
              
              {/* Invoice Details Row: Cargo Particulars & Weights */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {[
                  { key: 'description', label: 'Description', type: 'text' },
                  { key: 'net_weight', label: 'Net Weight', type: 'text' },
                  { key: 'gross_weight', label: 'Gross Weight', type: 'text' },
                  { key: 'no_of_packages', label: 'No. of Packages', type: 'text' },
                  { key: 'cbm_chargeable_weight', label: 'CBM/Chargeable Weight', type: 'text' },
                  { key: 'eway_bill_no', label: 'Eway Bill #', type: 'text' },
                ].map(f => (
                  <div key={f.key} className="space-y-1.5">
                    <Label className="text-text-dark font-ibm font-medium text-xs">{f.label}</Label>
                    <Input 
                      type={f.type} 
                      data-testid={`inv-${invIdx}-${f.key}`} 
                      value={inv[f.key] || ''} 
                      onChange={e => updateInvoice(invIdx, f.key, e.target.value)} 
                      className="h-9 text-sm border-gray-200" 
                      placeholder={f.label} 
                    />
                  </div>
                ))}
              </div>

              {/* Special Case Charges — AIR only */}
              {(mode === 'AIR' || mode === 'AIR_V2') && (
                <div className="flex items-center gap-4 pt-1 flex-wrap">
                  {/* Toggle */}
                  <label className="flex items-center gap-2 cursor-pointer select-none group">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={!!inv.special_case_charges}
                      onClick={() => {
                        const turningOn   = !inv.special_case_charges;
                        const grossWt     = parseFloat(inv.gross_weight)          || 0;
                        const airVolWt    = parseFloat(inv.cbm_chargeable_weight) || 0;
                        const autoWt      = Math.max(grossWt, airVolWt);
                        const newInvs     = invoices.map((it, i) => i === invIdx ? {
                          ...it,
                          special_case_charges:  turningOn,
                          // Auto-fill only when turning ON and field is currently empty
                          airline_actual_weight: turningOn
                            ? (it.airline_actual_weight || (autoWt > 0 ? autoWt.toFixed(2) : ''))
                            : it.airline_actual_weight,
                        } : it);
                        onChange({ ...payload, invoices: newInvs, package_info: computePackageInfo(newInvs) });
                      }}
                      data-testid={`inv-${invIdx}-special-case-toggle`}
                      className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none ${inv.special_case_charges ? 'bg-amber-500' : 'bg-gray-300'}`}
                    >
                      <span className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${inv.special_case_charges ? 'translate-x-4' : 'translate-x-0'}`} />
                    </button>
                    <span className="text-xs font-ibm text-text-dark font-medium">Special Case Charges</span>
                    <span className="text-[10px] text-gray-400 font-ibm hidden sm:inline">{isSea ? '(shipping line charges for container space)' : '(airline charges for wasted hold space)'}</span>
                  </label>

                  {/* Airline Actual Weight — editable, shown when special case is on */}
                  {!!inv.special_case_charges && (
                    <div className="space-y-0.5">
                      <Label className="text-xs font-ibm text-amber-700 font-semibold">{isSea ? 'Shipping Line Actual Weight (kg)' : 'Airline Actual Weight (kg)'}</Label>
                      <div className="flex items-center gap-1.5">
                        <Input
                          data-testid={`inv-${invIdx}-airline-actual-weight`}
                          value={inv.airline_actual_weight || ''}
                          onChange={e => updateInvoice(invIdx, 'airline_actual_weight', e.target.value)}
                          placeholder="Enter weight"
                          className="h-8 w-36 text-sm bg-amber-50 text-amber-800 font-semibold border-amber-200 focus:border-amber-400"
                        />
                        <span className="text-[10px] text-gray-400 font-ibm whitespace-nowrap">
                          = MAX(Gross {(parseFloat(inv.gross_weight)||0).toFixed(2)}, Vol. {(parseFloat(inv.cbm_chargeable_weight)||0).toFixed(2)})
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Dimension Repeater for this invoice */}
              <div className="space-y-3 pt-2">
                <div className="flex items-center gap-3 flex-wrap">
                  <Button 
                    type="button" 
                    size="sm" 
                    variant="outline" 
                    onClick={() => addInvoiceDimension(invIdx)} 
                    className="h-7 text-xs border-emerald-300 text-emerald-600 hover:bg-emerald-50" 
                    data-testid={`add-dimension-${invIdx}`}
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add Dimension
                  </Button>
                  {/* Dimension unit selector — select once, applies to all rows */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 font-ibm">Unit:</span>
                    <select
                      data-testid={`inv-${invIdx}-dimension_unit`}
                      value={inv.dimension_unit || 'CM'}
                      onChange={e => updateDimensionUnit(invIdx, e.target.value)}
                      className="h-7 text-xs border border-gray-200 rounded-md px-2 font-ibm bg-white text-text-dark focus:outline-none focus:ring-1 focus:ring-emerald-400"
                    >
                      {DIMENSION_UNITS.map(u => (
                        <option key={u.value} value={u.value}>{u.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {(inv.dimensions || [{ ...EMPTY_DIMENSION }]).map((dim, dimIdx) => {
                  const isAir = mode === 'AIR' || mode === 'AIR_V2';
                  const isDomestic = header.international_domestic === 'Domestic';
                  const unit = inv.dimension_unit || 'CM';
                  const l = parseFloat(dim.length) || 0;
                  const w = parseFloat(dim.width)  || 0;
                  const h = parseFloat(dim.height) || 0;
                  const qty = parseFloat(dim.no_of_packages) || 0;
                  const intlVol = (isAir && !isDomestic) ? calcVolWeight(l, w, h, qty, unit, 6000) : '';
                  const domVol  = (isAir && isDomestic)  ? calcVolWeight(l, w, h, qty, unit, 5000) : '';
                  // grid: 5 data cols + 1 vol wt (air only) + 1 delete
                  const gridCols = isAir ? 'grid-cols-7' : 'grid-cols-6';
                  return (
                  <div key={dimIdx} className={`grid ${gridCols} gap-3 items-end`}>
                    {['length', 'width', 'height', 'no_of_packages', 'cbm_chargeable_weight'].map(f => {
                      const colLabel =
                        f === 'length' ? `Length (${unit})` :
                        f === 'width'  ? `Width (${unit})` :
                        f === 'height' ? `Height (${unit})` :
                        f === 'no_of_packages' ? 'No Of Packages' : 'CBM (m³)';
                      return (
                        <div key={f} className="space-y-1">
                          {dimIdx === 0 && <Label className="text-[10px] text-gray-500 font-ibm">{colLabel}</Label>}
                          <Input 
                            type="text" 
                            value={dim[f] || ''} 
                            onChange={f === 'cbm_chargeable_weight' ? undefined : e => updateInvoiceDimension(invIdx, dimIdx, f, e.target.value)}
                            readOnly={f === 'cbm_chargeable_weight'}
                            className={`h-8 text-xs border-gray-200 ${f === 'cbm_chargeable_weight' ? 'bg-emerald-50 text-emerald-700 font-medium cursor-not-allowed' : ''}`}
                            placeholder={f === 'cbm_chargeable_weight' ? 'Auto' : ''} 
                          />
                        </div>
                      );
                    })}
                    {/* Air-only: single vol wt column — intl or domestic based on docket type */}
                    {isAir && (
                      <div className="space-y-1">
                        {dimIdx === 0 && (
                          <Label className="text-[10px] text-gray-500 font-ibm flex flex-col leading-tight">
                            <span>Air Vol. Wt. (kg)</span>
                            <span className="text-gray-400">{isDomestic ? '÷ 5000' : '÷ 6000'}</span>
                          </Label>
                        )}
                        <Input
                          readOnly
                          value={isDomestic ? domVol : intlVol}
                          placeholder="Auto"
                          className={`h-8 text-xs font-medium cursor-not-allowed border-gray-200 ${isDomestic ? 'bg-orange-50 text-orange-700' : 'bg-blue-50 text-blue-700'}`}
                        />
                      </div>
                    )}
                    <div>
                      {(inv.dimensions || []).length > 1 && (
                        <Button 
                          type="button" 
                          size="icon" 
                          variant="ghost" 
                          onClick={() => removeInvoiceDimension(invIdx, dimIdx)} 
                          className="h-8 w-8 text-red-400 hover:text-red-600"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        )}
      </Card>

      {/* SECTION 1b — DELIVERY INFO (ROAD_RAIL and AIR_V2) */}
      {(mode === 'ROAD_RAIL' || mode === 'AIR_V2') && (
        <Card id="sec-delivery" className="scroll-mt-32 bg-white rounded-xl border border-gray-100 shadow-sm">
          <div className="border-b-4 border-orange-500 px-6 py-4">
            <div className="flex items-center gap-3">
              <Truck className="w-5 h-5 text-orange-500" />
              <h2 className="text-lg font-semibold text-text-dark font-manrope">DELIVERY INFO</h2>
            </div>
          </div>
          <div className="p-6 space-y-4">
            {/* Row 1: LR No, LR Date, Vehicle Radio, Vehicle No, Vendor */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              <div className="space-y-1.5">
                <Label className="text-text-dark font-ibm font-medium text-xs">LR No.</Label>
                <Input data-testid="del-lr-no" value={deliveryInfo.lr_no || ''} onChange={e => updateSection('delivery_info', 'lr_no', e.target.value)} className="h-9 text-sm border-gray-200" placeholder="LR Number" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-text-dark font-ibm font-medium text-xs">LR Date</Label>
                <Input data-testid="del-lr-date" type="date" value={deliveryInfo.lr_date || ''} onChange={e => updateSection('delivery_info', 'lr_date', e.target.value)} className="h-9 text-sm border-gray-200" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-text-dark font-ibm font-medium text-xs">Vehicle</Label>
                <div className="flex items-center gap-4 h-9">
                  {['Stavan', 'Other'].map(opt => (
                    <label key={opt} className="flex items-center gap-1.5 cursor-pointer text-sm">
                      <input type="radio" name="vehicle_type" value={opt} checked={(deliveryInfo.vehicle_type || 'Stavan') === opt} onChange={() => updateSection('delivery_info', 'vehicle_type', opt)} className="accent-orange-500" data-testid={`del-vehicle-type-${opt.toLowerCase()}`} />
                      {opt}
                    </label>
                  ))}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-text-dark font-ibm font-medium text-xs">Vehicle No.</Label>
                <Input data-testid="del-vehicle-no" value={deliveryInfo.vehicle_no || ''} onChange={e => updateSection('delivery_info', 'vehicle_no', e.target.value)} className="h-9 text-sm border-gray-200" placeholder="e.g. GJ01AB1234" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-text-dark font-ibm font-medium text-xs">Vendor</Label>
                <Input data-testid="del-vendor" value={deliveryInfo.vendor || ''} onChange={e => updateSection('delivery_info', 'vendor', e.target.value)} className="h-9 text-sm border-gray-200" placeholder="Vendor name" />
              </div>
            </div>

            {/* Row 2: Loading From, Pickup Date, Clearance Origin, Date, Clearance Dest, Date */}
            <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
              <div className="space-y-1.5">
                <Label className="text-text-dark font-ibm font-medium text-xs">Loading From</Label>
                <MasterCombobox items={locationItems} value={deliveryInfo.loading_from} onSelect={v => updateSection('delivery_info', 'loading_from', v)} testId="del-loading-from" placeholder="Select Location" displayField="name" subtextField="subtext" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-text-dark font-ibm font-medium text-xs">Pick up Date</Label>
                <Input data-testid="del-pickup-date" type="date" value={deliveryInfo.pickup_date || ''} onChange={e => updateSection('delivery_info', 'pickup_date', e.target.value)} className="h-9 text-sm border-gray-200" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-text-dark font-ibm font-medium text-xs">Clearance At (Origin)</Label>
                <MasterCombobox items={locationItems} value={deliveryInfo.clearance_at_origin} onSelect={v => updateSection('delivery_info', 'clearance_at_origin', v)} testId="del-clearance-origin" placeholder="Select Location" displayField="name" subtextField="subtext" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-text-dark font-ibm font-medium text-xs">Clearance Date</Label>
                <Input data-testid="del-clearance-date-origin" type="date" value={deliveryInfo.clearance_date_origin || ''} onChange={e => updateSection('delivery_info', 'clearance_date_origin', e.target.value)} className="h-9 text-sm border-gray-200" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-text-dark font-ibm font-medium text-xs">Clearance At (Destination)</Label>
                <MasterCombobox items={locationItems} value={deliveryInfo.clearance_at_destination} onSelect={v => updateSection('delivery_info', 'clearance_at_destination', v)} testId="del-clearance-dest" placeholder="Select Location" displayField="name" subtextField="subtext" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-text-dark font-ibm font-medium text-xs">Clearance Date</Label>
                <Input data-testid="del-clearance-date-dest" type="date" value={deliveryInfo.clearance_date_destination || ''} onChange={e => updateSection('delivery_info', 'clearance_date_destination', e.target.value)} className="h-9 text-sm border-gray-200" />
              </div>
            </div>

            {/* Row 3: Delivery At, Delivery Date, Value, Weight, Packages/Type, Volume Weight */}
            <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
              <div className="space-y-1.5">
                <Label className="text-text-dark font-ibm font-medium text-xs">Delivery At</Label>
                <MasterCombobox items={locationItems} value={deliveryInfo.delivery_at} onSelect={v => updateSection('delivery_info', 'delivery_at', v)} testId="del-delivery-at" placeholder="Select Location" displayField="name" subtextField="subtext" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-text-dark font-ibm font-medium text-xs">Delivery Date</Label>
                <Input data-testid="del-delivery-date" type="date" value={deliveryInfo.delivery_date || ''} onChange={e => updateSection('delivery_info', 'delivery_date', e.target.value)} className="h-9 text-sm border-gray-200" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-text-dark font-ibm font-medium text-xs">Value</Label>
                <Input data-testid="del-value" value={deliveryInfo.value || ''} onChange={e => updateSection('delivery_info', 'value', e.target.value)} className="h-9 text-sm border-gray-200" placeholder="Declared value" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-text-dark font-ibm font-medium text-xs">Weight</Label>
                <Input data-testid="del-weight" value={deliveryInfo.weight || ''} onChange={e => updateSection('delivery_info', 'weight', e.target.value)} className="h-9 text-sm border-gray-200" placeholder="Weight" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-text-dark font-ibm font-medium text-xs">Packages / Type</Label>
                <Input data-testid="del-packages-type" value={deliveryInfo.packages_type || ''} onChange={e => updateSection('delivery_info', 'packages_type', e.target.value)} className="h-9 text-sm border-gray-200" placeholder="e.g. 10 Boxes" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-text-dark font-ibm font-medium text-xs">Volume Weight</Label>
                <Input data-testid="del-volume-weight" value={deliveryInfo.volume_weight || ''} onChange={e => updateSection('delivery_info', 'volume_weight', e.target.value)} className="h-9 text-sm border-gray-200" placeholder="Volume weight" />
              </div>
            </div>

            {/* Row 4: Door Delivery Date, Delivery Taken By, Description, Remarks */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-1.5">
                <Label className="text-text-dark font-ibm font-medium text-xs">Door Delivery Date</Label>
                <Input data-testid="del-door-delivery-date" type="date" value={deliveryInfo.door_delivery_date || ''} onChange={e => updateSection('delivery_info', 'door_delivery_date', e.target.value)} className="h-9 text-sm border-gray-200" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-text-dark font-ibm font-medium text-xs">Delivery Taken By</Label>
                <Input data-testid="del-delivery-taken-by" value={deliveryInfo.delivery_taken_by || ''} onChange={e => updateSection('delivery_info', 'delivery_taken_by', e.target.value)} className="h-9 text-sm border-gray-200" placeholder="Name" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-text-dark font-ibm font-medium text-xs">Description</Label>
                <Input data-testid="del-description" value={deliveryInfo.description || ''} onChange={e => updateSection('delivery_info', 'description', e.target.value)} className="h-9 text-sm border-gray-200" placeholder="Description" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-text-dark font-ibm font-medium text-xs">Remarks</Label>
                <Input data-testid="del-remarks" value={deliveryInfo.remarks || ''} onChange={e => updateSection('delivery_info', 'remarks', e.target.value)} className="h-9 text-sm border-gray-200" placeholder="Remarks" />
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* SECTION 3a — CONTAINER DETAILS (SEA only) */}
      {mode === 'SEA' && (
        <div id="sec-containers" className="scroll-mt-32">
        <ContainerDetailsSection
          displayContainers={displayContainers}
          containerDetails={containerDetails}
          addContainer={addContainer}
          removeContainer={removeContainer}
          updateContainer={updateContainer}
        />
        </div>
      )}

      {/* SECTION 3 — PACKAGE INFO */}
      <Card id="sec-package" className="scroll-mt-32 bg-white rounded-xl border border-gray-200/90 shadow-xs hover:border-gray-300 transition-all overflow-hidden">
        <div 
          className={`px-6 py-4 flex items-center justify-between cursor-pointer select-none transition-colors ${
            collapsedSections['sec-package'] ? 'bg-amber-50/30 hover:bg-amber-50/60 border-l-4 border-l-amber-500' : 'border-b-4 border-amber-500'
          }`}
          onClick={() => toggleSectionCollapse('sec-package')}
        >
          <div className="flex items-center gap-3">
            <Package className="w-5 h-5 text-amber-500" />
            <h2 data-testid="air-package-section" className="text-lg font-semibold text-text-dark font-manrope">PACKAGE INFO</h2>
          </div>
          <div className="flex items-center gap-3">
            {collapsedSections['sec-package'] && (
              <span className="text-xs font-ibm text-amber-950 bg-amber-50 border border-amber-200 px-3 py-1 rounded-full flex items-center gap-1.5 shadow-2xs">
                <span className="font-semibold">{header.package_type || 'Package'}</span>
                <span>· {computedPackageInfo.package_no_of_packages || '0'} Pkgs</span>
                <span>· {computedPackageInfo.package_gross_weight || '0'} kg GW</span>
                {effectiveChargeableWeight && <span className="text-amber-800">· {effectiveChargeableWeight} kg CW</span>}
              </span>
            )}
            <button type="button" className="text-gray-400 hover:text-gray-600 p-1">
              {collapsedSections['sec-package'] ? <ChevronDown className="w-5 h-5" /> : <ChevronUp className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {!collapsedSections['sec-package'] && (
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="space-y-1.5">
            <Label className="text-text-dark font-ibm font-medium text-xs">Package Type</Label>
            <select
              data-testid="package-type-select"
              value={header.package_type || ''}
              onChange={e => updateSection('header', 'package_type', e.target.value)}
              className="h-9 w-full border border-input rounded-md px-3 text-sm font-ibm bg-background focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">Select Package Type</option>
              <option value="BAG">BAG</option>
              <option value="BDL BUNDLE">BDL BUNDLE</option>
              <option value="BLK BULK">BLK BULK</option>
              <option value="Bottles">Bottles</option>
              <option value="Box">Box</option>
              <option value="CAN">CAN</option>
              <option value="Carbod Box">Carbod Box</option>
              <option value="CAS">CAS</option>
              <option value="CON CONTAINER">CON CONTAINER</option>
              <option value="CTN CARTOON">CTN CARTOON</option>
              <option value="DRM DRUM">DRM DRUM</option>
              <option value="Drum">Drum</option>
              <option value="Ganni Bags">Ganni Bags</option>
              <option value="JAR">JAR</option>
              <option value="ODC">ODC</option>
              <option value="Pallet">Pallet</option>
              <option value="Paper Bags">Paper Bags</option>
              <option value="PCS">PCS</option>
              <option value="PKG">PKG</option>
              <option value="Plastic Bags">Plastic Bags</option>
              <option value="PLT">PLT</option>
              <option value="ROL">ROL</option>
              <option value="SKD">SKD</option>
              <option value="UNIT">UNIT</option>
              <option value="Wooden Box">Wooden Box</option>
              <option value="Others">Others</option>
            </select>
          </div>
            {[
              { key: 'package_no_of_packages', label: 'No. of Packages' },
              { key: 'package_gross_weight', label: 'Gross Weight' },
              { key: 'package_net_weight', label: 'Net Weight' },
              { key: 'package_cbm_chargeable_weight', label: 'CBM/Chargeable Weight' },
            ].map(f => (
              <div key={f.key} className="space-y-1.5">
                <Label className="text-text-dark font-ibm font-medium text-xs">{f.label}</Label>
                <Input
                  type="text"
                  data-testid={`pkg-${f.key}`}
                  value={
                    isConsolidation && (f.key === 'package_no_of_packages' || f.key === 'package_gross_weight') && (linkedHouseDockets.length > 0)
                      ? (f.key === 'package_no_of_packages'
                          ? String(consolidation.total_packages || consolTotalPkgs || '')
                          : (consolTotalGw > 0 ? consolTotalGw.toFixed(2) : (consolidation.total_gross_weight ? Number(consolidation.total_gross_weight).toFixed(2) : '')))
                      : f.key === 'package_cbm_chargeable_weight'
                      ? effectiveChargeableWeight || computedPackageInfo[f.key] || ''
                      : computedPackageInfo[f.key] || ''
                  }
                  readOnly
                  className="h-9 text-sm border-gray-200 bg-gray-50 cursor-not-allowed font-medium"
                  placeholder="Auto"
                />
              </div>
            ))}
          </div>
          <p className="text-[10px] text-gray-400 mt-2 font-ibm">
            {isConsolidation && consolidation.house_dockets_summary?.length > 0
              ? `Auto-aggregated from ${consolidation.house_dockets_summary.length} attached House Docket(s)`
              : 'Auto-calculated from all invoice rows'}
          </p>
        </div>
        )}
      </Card>

      {/* SECTION 4 — SHIPMENT INFO (AIR / SEA only) */}
      {mode !== 'ROAD_RAIL' && (
      <Card id="sec-shipment" className="scroll-mt-32 bg-white rounded-xl border border-gray-200/90 shadow-xs hover:border-gray-300 transition-all overflow-hidden">
        <div 
          className={`px-6 py-4 flex items-center justify-between cursor-pointer select-none transition-colors ${
            collapsedSections['sec-shipment'] ? 'bg-purple-50/30 hover:bg-purple-50/60 border-l-4 border-l-purple-500' : 'border-b-4 border-violet-500'
          }`}
          onClick={() => toggleSectionCollapse('sec-shipment')}
        >
          <div className="flex items-center gap-3">
            {mode === 'SEA' ? <Ship className="w-5 h-5 text-purple-600" /> : <Plane className="w-5 h-5 text-purple-600" />}
            <h2 data-testid="air-shipment-section" className="text-lg font-semibold text-text-dark font-manrope">{mode === 'SEA' ? 'VOYAGE & SHIPMENT INFO' : 'FLIGHT & SHIPMENT INFO'}</h2>
          </div>
          <div className="flex items-center gap-3">
            {collapsedSections['sec-shipment'] && (
              <span className="text-xs font-ibm text-purple-950 bg-purple-50 border border-purple-200 px-3 py-1 rounded-full flex items-center gap-1.5 shadow-2xs">
                {shipmentInfo.carrier ? (carrierMap[shipmentInfo.carrier] || shipmentInfo.carrier) : (isSea ? 'Shipping Line Not Set' : 'Carrier Not Set')}
                {(shipmentInfo.loading_from || shipmentInfo.discharge_at) && (
                  <span className="font-semibold text-purple-700">
                    · {(locationMap[shipmentInfo.loading_from] || shipmentInfo.loading_from || '—')} ➔ {(locationMap[shipmentInfo.discharge_at] || shipmentInfo.discharge_at || '—')}
                  </span>
                )}
                {shipmentInfo.flight_no && (
                  <span className="text-purple-600">· {isSea ? 'Voy:' : 'Fl:'} {shipmentInfo.flight_no}</span>
                )}
              </span>
            )}
            <button type="button" className="text-gray-400 hover:text-gray-600 p-1">
              {collapsedSections['sec-shipment'] ? <ChevronDown className="w-5 h-5" /> : <ChevronUp className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {!collapsedSections['sec-shipment'] && (
        <div className="p-6 space-y-5">
          {/* AWB / BL Numbers */}
          {isAirV2 ? (
            /* AirV2: HAWB No.(3) + Date(3) + Description(6) on one row */
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-3">
                <FieldInput section="shipment_info" field="hawb_no" label={cfg.hawb_label} payload={payload} onChange={onChange} />
              </div>
              <div className="col-span-3">
                <FieldInput section="shipment_info" field="hawb_date" label={cfg.hawb_date_label} type="date" payload={payload} onChange={onChange} />
              </div>
              <div className="col-span-6 space-y-1.5">
                <Label className="text-text-dark font-ibm font-medium text-xs">{cfg.hawb_desc_label}</Label>
                <Textarea data-testid="ship-hawb-desc" value={shipmentInfo.hawb_description || ''} onChange={e => updateSection('shipment_info', 'hawb_description', e.target.value)} className="text-sm border-gray-200 min-h-[60px]" placeholder={cfg.hawb_desc_label} />
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {isConsolidation ? (
                /* Master Consolidation: MAWB details */
                <div className="rounded-lg border border-violet-200 bg-violet-50/40 p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-violet-700 uppercase tracking-wide">
                      {isSea ? 'MASTER BILL OF LADING (MBL)' : (cfg.mawb_label ? `MASTER ${cfg.mawb_label}` : 'MASTER AIR WAY BILL (MAWB)')}
                    </span>
                    <span className="text-xs text-violet-600 bg-violet-100 px-2 py-0.5 rounded-full font-medium">
                      {isSea ? 'Master Bill of Lading' : 'Master Airway Bill'}
                    </span>
                  </div>
                  <div className="grid grid-cols-12 gap-4">
                    <div className="col-span-3">
                      <FieldInput section="shipment_info" field="mawb_no"
                        label={cfg.mawb_label || 'MAWB No.'}
                        payload={payload} onChange={onChange} />
                    </div>
                    <div className="col-span-3">
                      <FieldInput section="shipment_info" field="mawb_date"
                        label={cfg.mawb_date_label || 'MAWB Date'}
                        type="date" payload={payload} onChange={onChange} />
                    </div>
                    <div className="col-span-6 space-y-1.5">
                      <Label className="text-text-dark font-ibm font-medium text-xs">{cfg.mawb_desc_label || 'MAWB Description'}</Label>
                      <Textarea data-testid="ship-mawb-desc" value={shipmentInfo.mawb_description || ''} onChange={e => updateSection('shipment_info', 'mawb_description', e.target.value)} className="text-sm border-gray-200 min-h-[60px]" placeholder={cfg.mawb_desc_label || (isSea ? 'Master Bill of Lading Description' : 'Master Airway Bill Description')} />
                    </div>
                  </div>
                </div>
              ) : isDirect ? (
                /* Direct Shipment: Only MAWB / MBL */
                <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-blue-800 uppercase tracking-wide">
                      {isSea ? 'SHIPPING LINE BILL OF LADING (MBL)' : (cfg.mawb_label || 'AIRLINE AIR WAY BILL (MAWB)')}
                    </span>
                    <span className="text-xs text-blue-700 bg-blue-100 px-2.5 py-0.5 rounded-full font-medium">
                      {isSea ? 'Direct Shipping Line Booking (No HBL)' : 'Direct Airline Booking (No HAWB)'}
                    </span>
                  </div>
                  <div className="grid grid-cols-12 gap-4">
                    <div className="col-span-3">
                      <FieldInput section="shipment_info" field="mawb_no"
                        label={isSea ? 'Shipping Line MBL No.' : (cfg.mawb_label || 'Airline MAWB No.')}
                        payload={payload} onChange={onChange} />
                    </div>
                    <div className="col-span-3">
                      <FieldInput section="shipment_info" field="mawb_date"
                        label={isSea ? 'Shipping Line MBL Date' : (cfg.mawb_date_label || 'Airline MAWB Date')}
                        type="date" payload={payload} onChange={onChange} />
                    </div>
                    <div className="col-span-6 space-y-1.5">
                      <Label className="text-text-dark font-ibm font-medium text-xs">{cfg.mawb_desc_label || 'MAWB Description'}</Label>
                      <Textarea data-testid="ship-mawb-desc" value={shipmentInfo.mawb_description || ''} onChange={e => updateSection('shipment_info', 'mawb_description', e.target.value)} className="text-sm border-gray-200 min-h-[60px]" placeholder={isSea ? 'Direct Shipping Line Bill of Lading Description' : 'Direct Airline Airway Bill Description'} />
                    </div>
                  </div>
                </div>
              ) : (
                /* Back-to-Back: Both HAWB/HBL and Carrier MAWB/MBL */
                <div className="space-y-4">
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50/30 p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-emerald-800 uppercase tracking-wide">
                        {isSea ? 'HOUSE BILL OF LADING (HBL) — Issued to Customer' : 'HOUSE AIR WAY BILL (HAWB) — Issued to Customer'}
                      </span>
                      <span className="text-xs text-emerald-700 bg-emerald-100 px-2.5 py-0.5 rounded-full font-medium">
                        {isSea ? 'House BL' : 'House AWB'}
                      </span>
                    </div>
                    <div className="grid grid-cols-12 gap-4">
                      <div className="col-span-3">
                        <FieldInput section="shipment_info" field="hawb_no"
                          label={cfg.hawb_label || 'HAWB No.'}
                          payload={payload} onChange={onChange} />
                      </div>
                      <div className="col-span-3">
                        <FieldInput section="shipment_info" field="hawb_date"
                          label={cfg.hawb_date_label || 'HAWB Date'}
                          type="date" payload={payload} onChange={onChange} />
                      </div>
                      <div className="col-span-6 space-y-1.5">
                        <Label className="text-text-dark font-ibm font-medium text-xs">{cfg.hawb_desc_label || 'HAWB Description'}</Label>
                        <Textarea data-testid="ship-hawb-desc" value={shipmentInfo.hawb_description || ''} onChange={e => updateSection('shipment_info', 'hawb_description', e.target.value)} className="text-sm border-gray-200 min-h-[60px]" placeholder={cfg.hawb_desc_label || (isSea ? 'House Bill of Lading Description' : 'House Airway Bill Description')} />
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg border border-blue-200 bg-blue-50/30 p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-blue-800 uppercase tracking-wide">
                        {isSea ? 'SHIPPING LINE MASTER BILL OF LADING (MBL) — Carrier Booking' : 'AIRLINE MASTER AIR WAY BILL (MAWB) — Carrier Booking'}
                      </span>
                      <span className="text-xs text-blue-700 bg-blue-100 px-2.5 py-0.5 rounded-full font-medium">
                        {isSea ? 'Shipping Line MBL' : 'Airline MAWB'}
                      </span>
                    </div>
                    <div className="grid grid-cols-12 gap-4">
                      <div className="col-span-3">
                        <FieldInput section="shipment_info" field="mawb_no"
                          label={isSea ? 'Shipping Line MBL No.' : (cfg.mawb_label || 'Airline MAWB No.')}
                          payload={payload} onChange={onChange} />
                      </div>
                      <div className="col-span-3">
                        <FieldInput section="shipment_info" field="mawb_date"
                          label={isSea ? 'Shipping Line MBL Date' : (cfg.mawb_date_label || 'Airline MAWB Date')}
                          type="date" payload={payload} onChange={onChange} />
                      </div>
                      <div className="col-span-6 space-y-1.5">
                        <Label className="text-text-dark font-ibm font-medium text-xs">{cfg.mawb_desc_label || (isSea ? 'Shipping Line MBL Description' : 'Airline AWB Description')}</Label>
                        <Textarea data-testid="ship-airline-awb-desc" value={shipmentInfo.mawb_description || ''} onChange={e => updateSection('shipment_info', 'mawb_description', e.target.value)} className="text-sm border-gray-200 min-h-[60px]" placeholder={cfg.mawb_desc_label || (isSea ? 'Shipping Line MBL Description' : 'Airline AWB Description')} />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}


          {/* ── Sub-Section A: Routing & Location Milestones (Origin vs Destination) ── */}
          <div className="space-y-3 pt-2">
            <div className="flex items-center gap-2 pb-1 border-b border-gray-100">
              <span className="text-xs font-bold text-gray-700 uppercase tracking-wider font-ibm flex items-center gap-1.5">
                📍 Routing &amp; Transit Milestones
              </span>
              <span className="text-[11px] text-gray-400 font-ibm">({isSea ? 'Port & Terminal location pairs' : 'Origin & Destination location pairs'})</span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {/* Left Column: Origin & Loading */}
              <div className="p-4 rounded-xl border border-sky-200/80 bg-sky-50/30 space-y-3.5 shadow-xs">
                <div className="flex items-center gap-2 pb-2 border-b border-sky-100">
                  <span className="text-xs font-bold text-sky-900 uppercase tracking-wide flex items-center gap-1.5 font-ibm">
                    {isSea ? '🚢 Origin & Port of Loading' : '🛫 Origin & Departure'}
                  </span>
                </div>

                {/* Place of Receipts */}
                <div className="grid grid-cols-12 gap-3 items-end">
                  <div className="col-span-7 space-y-1">
                    <Label className="text-text-dark font-ibm font-medium text-xs">Place of Receipts</Label>
                    <MasterCombobox items={locationItems} value={shipmentInfo.place_of_receipts} onSelect={v => updateSection('shipment_info', 'place_of_receipts', v)} testId="ship-place-of-receipts" placeholder="Select Location" displayField="name" subtextField="subtext" />
                  </div>
                  <div className="col-span-5">
                    <FieldInput section="shipment_info" field="receipt_date" label="Receipt Date" type="date" payload={payload} onChange={onChange} />
                  </div>
                </div>

                {/* Loading From */}
                <div className="grid grid-cols-12 gap-3 items-end">
                  <div className="col-span-7 space-y-1">
                    <Label className="text-text-dark font-ibm font-medium text-xs">{isSea ? 'Port of Loading (POL)' : 'Loading From (Airport/Origin)'}</Label>
                    <MasterCombobox items={locationItems} value={shipmentInfo.loading_from} onSelect={v => updateSection('shipment_info', 'loading_from', v)} testId="ship-loading-from" placeholder="Select Location" displayField="name" subtextField="subtext" />
                  </div>
                  <div className="col-span-5">
                    <FieldInput section="shipment_info" field="loading_date" label={isSea ? 'Loading Date (ETD)' : 'Loading Date'} type="date" payload={payload} onChange={onChange} />
                  </div>
                </div>

                {/* Clearance At Origin */}
                <div className="grid grid-cols-12 gap-3 items-end">
                  <div className="col-span-7 space-y-1">
                    <Label className="text-text-dark font-ibm font-medium text-xs">Clearance At (Origin)</Label>
                    <MasterCombobox items={locationItems} value={shipmentInfo.clearance_at_origin} onSelect={v => updateSection('shipment_info', 'clearance_at_origin', v)} testId="ship-clearance-origin" placeholder="Select Location" displayField="name" subtextField="subtext" />
                  </div>
                  <div className="col-span-5">
                    <FieldInput section="shipment_info" field="clearance_date_origin" label="Clearance Date" type="date" payload={payload} onChange={onChange} />
                  </div>
                </div>
              </div>

              {/* Right Column: Destination & Delivery */}
              <div className="p-4 rounded-xl border border-indigo-200/80 bg-indigo-50/30 space-y-3.5 shadow-xs">
                <div className="flex items-center gap-2 pb-2 border-b border-indigo-100">
                  <span className="text-xs font-bold text-indigo-900 uppercase tracking-wide flex items-center gap-1.5 font-ibm">
                    {isSea ? '⚓ Destination & Port of Discharge' : '🛬 Destination & Arrival'}
                  </span>
                </div>

                {/* Discharge At */}
                <div className="grid grid-cols-12 gap-3 items-end">
                  <div className="col-span-7 space-y-1">
                    <Label className="text-text-dark font-ibm font-medium text-xs">{isSea ? 'Port of Discharge (POD)' : 'Discharge At (Airport/Destination)'}</Label>
                    <MasterCombobox items={locationItems} value={shipmentInfo.discharge_at} onSelect={v => updateSection('shipment_info', 'discharge_at', v)} testId="ship-discharge-at" placeholder="Select Location" displayField="name" subtextField="subtext" />
                  </div>
                  <div className="col-span-5">
                    <FieldInput section="shipment_info" field="discharge_date" label={isSea ? 'Discharge Date (ETA)' : 'Discharge Date'} type="date" payload={payload} onChange={onChange} />
                  </div>
                </div>

                {/* Delivery At */}
                <div className="grid grid-cols-12 gap-3 items-end">
                  <div className="col-span-7 space-y-1">
                    <Label className="text-text-dark font-ibm font-medium text-xs">{isSea ? 'Delivery At (Final Destination / CFS)' : 'Delivery At (Final Destination)'}</Label>
                    <MasterCombobox items={locationItems} value={shipmentInfo.delivery_at} onSelect={v => updateSection('shipment_info', 'delivery_at', v)} testId="ship-delivery-at" placeholder="Select Location" displayField="name" subtextField="subtext" />
                  </div>
                  <div className="col-span-5">
                    <FieldInput section="shipment_info" field="delivery_date" label="Delivery Date" type="date" payload={payload} onChange={onChange} />
                  </div>
                </div>

                {/* Clearance At Destination */}
                <div className="grid grid-cols-12 gap-3 items-end">
                  <div className="col-span-7 space-y-1">
                    <Label className="text-text-dark font-ibm font-medium text-xs">Clearance At (Destination)</Label>
                    <MasterCombobox items={locationItems} value={shipmentInfo.clearance_at_destination} onSelect={v => updateSection('shipment_info', 'clearance_at_destination', v)} testId="ship-clearance-dest" placeholder="Select Location" displayField="name" subtextField="subtext" />
                  </div>
                  <div className="col-span-5">
                    <FieldInput section="shipment_info" field="clearance_date_destination" label="Clearance Date" type="date" payload={payload} onChange={onChange} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── Sub-Section B: Carrier, Agent & Flight Details ── */}
          <div className="p-4 rounded-xl border border-gray-200/90 bg-gray-50/50 space-y-3.5 shadow-xs">
            <div className="flex items-center justify-between pb-2 border-b border-gray-200/70">
              <span className="text-xs font-bold text-gray-800 uppercase tracking-wide flex items-center gap-1.5 font-ibm">
                {isSea ? '🚢 Shipping Line, Handling Agent & Vessel' : '✈️ Carrier, Handling Agent & Flight'}
              </span>
              <span className="text-[11px] text-gray-500 font-ibm">{isSea ? 'Shipping Line & Vessel Schedule' : 'Line & Flight Schedule'}</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-1">
                <Label className="text-text-dark font-ibm font-medium text-xs">{isSea ? 'Shipping Line' : 'Carrier'}</Label>
                <MasterCombobox items={carrierItems} value={shipmentInfo.carrier} onSelect={v => updateSection('shipment_info', 'carrier', v)} testId="ship-carrier" placeholder={cfg.carrier_placeholder} displayField="name" subtextField="subtext" />
              </div>
              <div className="space-y-1">
                <Label className="text-text-dark font-ibm font-medium text-xs">Destination Agent</Label>
                <MasterCombobox items={agentItems} value={shipmentInfo.agent} onSelect={v => updateSection('shipment_info', 'agent', v)} testId="ship-agent" placeholder="Select Agent" displayField="name" subtextField="subtext" />
              </div>
              <div className="space-y-1">
                <Label className="text-text-dark font-ibm font-medium text-xs">Prepaid / Collect</Label>
                <Select value={shipmentInfo.prepaid_collect || ''} onValueChange={v => updateSection('shipment_info', 'prepaid_collect', v)}>
                  <SelectTrigger data-testid="ship-prepaid-collect" className="h-9 text-sm bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Prepaid">Prepaid</SelectItem>
                    <SelectItem value="Collect">Collect</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {!isInward ? (
                <div className="space-y-1">
                  <Label className="text-text-dark font-ibm font-medium text-xs">Freighted Status</Label>
                  <Select value={shipmentInfo.freighted_status || ''} onValueChange={v => updateSection('shipment_info', 'freighted_status', v)}>
                    <SelectTrigger data-testid="ship-freighted-status" className="h-9 text-sm bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Freighted">Freighted</SelectItem>
                      <SelectItem value="Unfreighted">Unfreighted</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="space-y-1">
                  <FieldInput section="shipment_info" field="igm_no" label="IGM No." payload={payload} onChange={onChange} />
                </div>
              )}
            </div>

            {/* Flight info and inward extras */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-1">
              <FieldInput section="shipment_info" field="flight_no" label={cfg.vessel_label} payload={payload} onChange={onChange} />
              <FieldInput section="shipment_info" field="flight_date" label={cfg.vessel_date_label} type="date" payload={payload} onChange={onChange} />
              <FieldInput section="shipment_info" field="flight_remarks" label={cfg.vessel_remarks_label} payload={payload} onChange={onChange} />
            </div>

            {!isAirV2 && isInward && (
              <div className="grid grid-cols-2 gap-4 pt-1">
                <FieldInput section="shipment_info" field="igm_date" label="IGM Date" type="date" payload={payload} onChange={onChange} />
              </div>
            )}
          </div>

          {/* ── Sub-Section C: Freight & Charges Breakdown ── */}
          {(isInward || shipmentInfo.freighted_status === 'Freighted') && (
            <div className="p-4 rounded-xl border border-amber-200/90 bg-amber-50/30 space-y-4 shadow-xs">
              <div className="flex items-center justify-between pb-2 border-b border-amber-200/70">
                <span className="text-xs font-bold text-amber-950 uppercase tracking-wide flex items-center gap-1.5 font-ibm">
                  💰 Freight &amp; Fee Charges Breakdown
                </span>
                <span className="text-[11px] text-amber-800 font-ibm">Freight Rates &amp; Other Charges</span>
              </div>

              {/* Freight Rate and Amounts */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 bg-white/80 p-3 rounded-lg border border-amber-100">
                {!isInward && (
                  <div className="space-y-1">
                    <Label className="text-text-dark font-ibm font-medium text-xs">Freight Rate</Label>
                    <Input
                      data-testid="ship-freight-rate"
                      type="number"
                      className="h-9 text-sm border-gray-200 bg-white"
                      placeholder="Rate per unit"
                      value={shipmentInfo.freight_rate || ''}
                      onChange={e => {
                        const rate = e.target.value;
                        const cbw  = parseFloat(effectiveChargeableWeight || computedPackageInfo.package_cbm_chargeable_weight) || 0;
                        const amt  = rate && cbw ? (parseFloat(rate) * cbw).toFixed(2) : '';
                        const next = { ...payload, shipment_info: { ...(payload.shipment_info || {}), freight_rate: rate, freight_amount: amt } };
                        onChange(next);
                      }}
                    />
                  </div>
                )}
                <div className="space-y-1">
                  <Label className="text-text-dark font-ibm font-medium text-xs">Freight Amount</Label>
                  <Input
                    data-testid="ship-freight-amount"
                    type="text"
                    className={`h-9 text-sm border-gray-200 ${!isInward ? 'bg-gray-50 cursor-not-allowed' : 'bg-white'}`}
                    placeholder="Freight Amount"
                    value={shipmentInfo.freight_amount || ''}
                    readOnly={!isInward}
                    onChange={isInward ? e => updateSection('shipment_info', 'freight_amount', e.target.value) : undefined}
                  />
                </div>
                <div className="space-y-1">
                  <FieldSelect section="shipment_info" field="freight_currency" label="Freight Currency" options={['INR', 'USD', 'EUR', 'GBP', 'AED', 'SGD', 'JPY', 'CNY']} payload={payload} onChange={onChange} />
                </div>
                {isInward && (
                  <div className="space-y-1">
                    <FieldInput section="shipment_info" field="ex_work_charges" label="Ex-work Charges" payload={payload} onChange={onChange} />
                  </div>
                )}
              </div>

              {/* Total Other Charges Due Agent */}
              <div className="space-y-2 bg-white/70 p-3 rounded-lg border border-gray-200/80">
                <div className="flex items-center justify-between">
                  <Label className="text-text-dark font-ibm font-semibold text-xs">Total Other Charges Due Agent</Label>
                  <span className="text-[10px] text-gray-500 font-ibm">Prepaid or Collect based on Payment Mode</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
                  {(isSea ? ['BL', 'PCA', 'OTH', 'MCC', 'HBL'] : ['AWB', 'PCA', 'OTH', 'MCC', 'HAWB']).map(code => (
                    <div key={code} className="flex items-center gap-1.5 bg-gray-50/80 px-2 py-1 rounded-md border border-gray-200">
                      <span className="text-xs font-bold text-gray-600 w-10 shrink-0">{code}:</span>
                      <Input
                        data-testid={`other-charge-${code.toLowerCase()}`}
                        type="number"
                        className="h-7 text-xs border-0 bg-transparent text-right font-medium focus-visible:ring-0 p-0"
                        placeholder="0.00"
                        value={(payload.shipment_info?.other_charges || {})[code] || ''}
                        onChange={e => {
                          const prev = payload.shipment_info?.other_charges || {};
                          const next = { ...payload, shipment_info: { ...(payload.shipment_info || {}), other_charges: { ...prev, [code]: e.target.value } } };
                          onChange(next);
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Total Other Charges Due Carrier */}
              <div className="space-y-2 bg-white/70 p-3 rounded-lg border border-gray-200/80">
                <div className="flex items-center justify-between">
                  <Label className="text-text-dark font-ibm font-semibold text-xs">{isSea ? 'Total Other Charges Due Shipping Line' : 'Total Other Charges Due Carrier'}</Label>
                  <span className="text-[10px] text-gray-500 font-ibm">{isSea ? 'Shipping line fee tariff schedule' : 'Carrier fee tariff schedule'}</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
                  {(isSea ? ['MCC', 'THC', 'DI CHGS', 'CGC', 'AMS', 'DOC', 'MBL', 'HBL', 'DTC', 'SUR', 'ASI MBL', 'ASI HBL'] : ['MCC', 'X-RAY', 'DI CHGS', 'CGC', 'AMS', 'AWC', 'MAWB', 'HAWB', 'DTC', 'GMAX', 'ASI MAWB', 'ASI HAWB']).map(code => (
                    <div key={code} className="flex items-center gap-1.5 bg-gray-50/80 px-2 py-1 rounded-md border border-gray-200">
                      <span className="text-xs font-bold text-gray-600 shrink-0" style={{minWidth: '58px'}} title={code}>{code}:</span>
                      <Input
                        data-testid={`carrier-charge-${code.toLowerCase().replace(/\s+/g, '-')}`}
                        type="number"
                        className="h-7 text-xs border-0 bg-transparent text-right font-medium focus-visible:ring-0 p-0"
                        placeholder="0.00"
                        value={(payload.shipment_info?.carrier_charges || {})[code] || ''}
                        onChange={e => {
                          const prev = payload.shipment_info?.carrier_charges || {};
                          const next = { ...payload, shipment_info: { ...(payload.shipment_info || {}), carrier_charges: { ...prev, [code]: e.target.value } } };
                          onChange(next);
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
        )}
      </Card>
      )} {/* end mode !== ROAD_RAIL for Section 4 */}

      {/* SECTION 5 — TECHNICAL DETAILS (Hidden for General/Valuable) */}
      {showTechnicalDetails && (
        <Card id="sec-technical" className="scroll-mt-32 bg-white rounded-xl border border-gray-200/90 shadow-xs hover:border-gray-300 transition-all overflow-hidden">
          <div 
            className={`px-6 py-4 flex items-center justify-between cursor-pointer select-none transition-colors ${
              collapsedSections['sec-technical'] ? 'bg-slate-50/50 hover:bg-slate-100/60 border-l-4 border-l-slate-500' : 'border-b-4 border-slate-500'
            }`}
            onClick={() => toggleSectionCollapse('sec-technical')}
          >
            <div className="flex items-center gap-3">
              <Settings2 className="w-5 h-5 text-slate-600" />
              <h2 data-testid="air-technical-section" className="text-lg font-semibold text-text-dark font-manrope">TECHNICAL DETAILS</h2>
            </div>
            <div className="flex items-center gap-3">
              {collapsedSections['sec-technical'] && (
                <span className="text-xs font-ibm text-slate-800 bg-slate-100 border border-slate-200 px-3 py-1 rounded-full flex items-center gap-1.5 shadow-2xs">
                  <span>Cargo: {header.cargo_type || 'General'}</span>
                  {payload.technical_details?.packaging && <span className="text-slate-600">· {payload.technical_details.packaging}</span>}
                </span>
              )}
              <button type="button" className="text-gray-400 hover:text-gray-600 p-1">
                {collapsedSections['sec-technical'] ? <ChevronDown className="w-5 h-5" /> : <ChevronUp className="w-5 h-5" />}
              </button>
            </div>
          </div>
          {!collapsedSections['sec-technical'] && (
          <div className="p-6 space-y-5">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <FieldInput section="technical_details" field="temperature" label="Temperature" payload={payload} onChange={onChange} />
              <FieldSelect section="technical_details" field="packaging" label="Packaging" options={['Standard', 'Custom', 'Specialized', 'Stavan Client', 'None']} payload={payload} onChange={onChange} />
              <FieldSelect section="technical_details" field="recover_packing" label="Recover Packing" options={['Yes', 'No', 'N/A']} payload={payload} onChange={onChange} />
              <FieldSelect section="technical_details" field="data_logger" label="Data Logger" options={['Yes', 'No', 'N/A']} payload={payload} onChange={onChange} />
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              <FieldSelect section="technical_details" field="data_logger_supply_by" label="Data Logger Supply By" options={['Shipper', 'Consignee', 'Agent', 'Stavan', 'Client', 'N/A']} payload={payload} onChange={onChange} />
              <FieldSelect section="technical_details" field="logger_recover" label="Logger Recover" options={['Yes', 'No', 'N/A']} payload={payload} onChange={onChange} />
              <FieldSelect section="technical_details" field="logger_reading" label="Logger Reading" options={['Yes', 'No', 'N/A']} payload={payload} onChange={onChange} />
              <FieldSelect section="technical_details" field="dry_ice" label="Dry Ice" options={['Yes', 'No', 'N/A']} payload={payload} onChange={onChange} />
              <FieldSelect section="technical_details" field="pickup_delivery_vehicle" label="Pick up / Delivery Vehicle" options={['Reefer', 'Dry Van', 'Open', 'TCV', 'Normal', 'Closed Body', 'N/A']} payload={payload} onChange={onChange} />
            </div>
          </div>
          )}
        </Card>
      )}

      {/* SECTION 6 — CONDITIONAL: TEMPERATURE / LOGGER BLOCK */}
      {showTempLogger && (
        <Card id="sec-temp" className="scroll-mt-32 bg-white rounded-xl border border-gray-200/90 shadow-xs hover:border-gray-300 transition-all overflow-hidden">
          <div 
            className={`px-6 py-4 flex items-center justify-between cursor-pointer select-none transition-colors ${
              collapsedSections['sec-temp'] ? 'bg-amber-50/30 hover:bg-amber-50/60 border-l-4 border-l-amber-500' : 'border-b-4 border-amber-400'
            }`}
            onClick={() => toggleSectionCollapse('sec-temp')}
          >
            <div className="flex items-center gap-3">
              <Thermometer className="w-5 h-5 text-amber-500" />
              <h2 data-testid="air-temp-logger-section" className="text-lg font-semibold text-text-dark font-manrope">TEMPERATURE / LOGGER DETAILS</h2>
            </div>
            <div className="flex items-center gap-3">
              {collapsedSections['sec-temp'] && (
                <span className="text-xs font-ibm text-amber-900 bg-amber-50 border border-amber-200 px-3 py-1 rounded-full flex items-center gap-1.5 shadow-2xs">
                  <span>Logger: {payload.temperature_logger_block?.data_logger_sr_no || 'Pending'}</span>
                  {payload.temperature_logger_block?.temperature_report_download && <span className="text-amber-700">· {payload.temperature_logger_block.temperature_report_download}</span>}
                </span>
              )}
              <button type="button" className="text-gray-400 hover:text-gray-600 p-1">
                {collapsedSections['sec-temp'] ? <ChevronDown className="w-5 h-5" /> : <ChevronUp className="w-5 h-5" />}
              </button>
            </div>
          </div>
          {!collapsedSections['sec-temp'] && (
          <div className="p-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-1">
                <label className="text-xs font-medium text-text-secondary uppercase tracking-wide">
                  Data Logger Sr No.
                </label>
                <DataLoggerSelector
                  value={tempLogger.data_logger_sr_no}
                  loggerSource={tempLogger.data_logger_source}
                  loggerItemId={tempLogger.data_logger_item_id}
                  onChange={({ data_logger_no, data_logger_source, data_logger_item_id }) => {
                    onChange({
                      ...payload,
                      temperature_logger_block: {
                        ...(payload.temperature_logger_block || {}),
                        data_logger_sr_no: data_logger_no,
                        data_logger_source,
                        data_logger_item_id,
                      }
                    });
                  }}
                  inventoryItems={inventoryItems}
                  onInventoryItemsChange={onInventoryItemsChange}
                  excludeDocketId={excludeDocketId || currentDocketId}
                />
              </div>
              <FieldInput section="temperature_logger_block" field="data_logger_date" label="Date" type="date" payload={payload} onChange={onChange} />
              <FieldInput section="temperature_logger_block" field="start_by" label="Start By" payload={payload} onChange={onChange} />
              <FieldSelect section="temperature_logger_block" field="temperature_report_download" label="Temperature Report Download" options={['Pending', 'Downloaded', 'N/A']} payload={payload} onChange={onChange} />
            </div>
          </div>
          )}
        </Card>
      )}

      {/* SECTION 7 — CONDITIONAL: HAZ BLOCK */}
      {showHazBlock && (
        <Card id="sec-haz" className="scroll-mt-32 bg-white rounded-xl border border-gray-200/90 shadow-xs hover:border-gray-300 transition-all overflow-hidden">
          <div 
            className={`px-6 py-4 flex items-center justify-between cursor-pointer select-none transition-colors ${
              collapsedSections['sec-haz'] ? 'bg-red-50/30 hover:bg-red-50/60 border-l-4 border-l-red-500' : 'border-b-4 border-red-500'
            }`}
            onClick={() => toggleSectionCollapse('sec-haz')}
          >
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              <h2 data-testid="air-haz-section" className="text-lg font-semibold text-text-dark font-manrope">HAZARDOUS MATERIAL DETAILS</h2>
            </div>
            <div className="flex items-center gap-3">
              {collapsedSections['sec-haz'] && (
                <span className="text-xs font-ibm text-red-900 bg-red-50 border border-red-200 px-3 py-1 rounded-full flex items-center gap-1.5 shadow-2xs">
                  <span>Class: {payload.haz_block?.haz_class || '—'}</span>
                  {payload.haz_block?.un_no && <span className="text-red-700">· UN: {payload.haz_block.un_no}</span>}
                </span>
              )}
              <button type="button" className="text-gray-400 hover:text-gray-600 p-1">
                {collapsedSections['sec-haz'] ? <ChevronDown className="w-5 h-5" /> : <ChevronUp className="w-5 h-5" />}
              </button>
            </div>
          </div>
          {!collapsedSections['sec-haz'] && (
          <div className="p-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <FieldInput section="haz_block" field="haz_class" label="HAZ Class" payload={payload} onChange={onChange} />
              <FieldInput section="haz_block" field="proper_shipping_name" label="Proper Shipping Name" payload={payload} onChange={onChange} />
              <FieldInput section="haz_block" field="packing_group" label="Packing Group" payload={payload} onChange={onChange} />
              <FieldInput section="haz_block" field="un_no" label="UN No" payload={payload} onChange={onChange} />
            </div>
          </div>
          )}
        </Card>
      )}

      {/* House Docket Picker Modal */}
      <HouseDocketConsolidationPicker
        isOpen={isPickerOpen}
        onClose={() => setIsPickerOpen(false)}
        companyId={companyId}
        currentDocketId={currentDocketId}
        shipmentType={shipmentType || mode}
        selectedDocketIds={consolidation.linked_house_docket_ids || []}
        onConfirm={handleConfirmHouseDockets}
      />
    </div>
  );
};

// Reusable inline field components
const FieldInput = ({ section, field, label, type = 'text', payload, onChange }) => {
  const value = (payload[section] || {})[field] || '';
  const update = (v) => onChange({ ...payload, [section]: { ...(payload[section] || {}), [field]: v } });
  return (
    <div className="space-y-1.5">
      <Label className="text-text-dark font-ibm font-medium text-xs">{label}</Label>
      <Input type={type} data-testid={`${section.replace(/_/g, '-')}-${field.replace(/_/g, '-')}`} value={value} onChange={e => update(e.target.value)} className="h-9 text-sm border-gray-200" placeholder={label} />
    </div>
  );
};

const FieldSelect = ({ section, field, label, options, payload, onChange }) => {
  const value = (payload[section] || {})[field] || '';
  const update = (v) => onChange({ ...payload, [section]: { ...(payload[section] || {}), [field]: v } });
  return (
    <div className="space-y-1.5">
      <Label className="text-text-dark font-ibm font-medium text-xs">{label}</Label>
      <Select value={value} onValueChange={update}>
        <SelectTrigger data-testid={`${section.replace(/_/g, '-')}-${field.replace(/_/g, '-')}`} className="h-9 text-sm"><SelectValue placeholder="-- Select --" /></SelectTrigger>
        <SelectContent>
          {options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
};

export default AirJobSheetForm;
